import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import * as THREE from 'three';
import { PerspectivePoints, Point2D, SurfaceHotspot, TargetSurface, ViewerMode } from '../../models/visualizer.models';

type CornerKey = keyof PerspectivePoints;
type SurfacePattern =
  | 'straight'
  | 'third'
  | 'random'
  | 'grid'
  | 'offset'
  | 'brick'
  | 'brick_1_3'
  | 'mix'
  | 'chessboard'
  | 'herringbone'
  | 'chevron'
  | 'basket'
  | 'basketweave'
  | 'tile';
type ScreenHotspot = SurfaceHotspot & {
  x: number;
  y: number;
  polygonPoints: string;
  polygonPointList: Array<Point2D & { label: string }>;
};

interface LayerBBox {
  x1: number;
  y1: number;
  x2?: number;
  y2?: number;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
}

interface PreviewLayerInput {
  id: string;
  surface: TargetSurface;
  instanceId?: string | null;
  label?: string | null;
  wallRole?: string | null;
  textureUrl: string;
  maskUrl: string;
  maskFlipY?: boolean;
  sliceUrl?: string | null;
  lightTextureUrl?: string | null;
  perspectivePoints: PerspectivePoints | null;
  polygon?: Point2D[] | null;
  bbox?: LayerBBox | null;
  opacity?: number;
  rotationAngle?: number;
  textureScale?: number;
  textureOffsetX?: number;
  textureOffsetY?: number;
  pattern?: string;
  plankWidthM?: number;
  plankLengthM?: number;
  groutWidthM?: number;
  groutEnabled?: boolean;
  groutSizeMm?: number;
  groutColor?: string;
  roomWidthM?: number;
  roomDepthM?: number;
  surfaceWidthMm?: number | null;
  surfaceHeightMm?: number | null;
  surfaceLengthMm?: number | null;
  surfaceDepthMm?: number | null;
  productWidthMm?: number | null;
  productHeightMm?: number | null;
  pixelsPerMeter?: number;
}

interface HerringboneCell {
  points: Point2D[];
  orientation: 1 | 2;
}

interface HerringboneTileMetrics {
  tileSizePx: number;
  tileSizeM: number;
  pixelsPerMeter: number;
  plankW: number;
  plankL: number;
}

interface ChevronTileMetrics {
  tileWidthPx: number;
  tileHeightPx: number;
  tileWidthM: number;
  tileHeightM: number;
  pixelsPerMeter: number;
  plankW: number;
  plankL: number;
  cutPlankL: number;
  halfColumnW: number;
  columnW: number;
  rowStep: number;
}

interface ChevronPlankPolygon {
  points: Point2D[];
  side: 'left' | 'right';
  center: Point2D;
}

interface BrickTileMetrics {
  tileWidthPx: number;
  tileHeightPx: number;
  tileWidthM: number;
  tileHeightM: number;
  pixelsPerMeter: number;
  plankW: number;
  plankL: number;
  rowOffsetRatios: number[];
  tileColumnCount: number;
}

interface GridTileMetrics {
  tileWidthPx: number;
  tileHeightPx: number;
  tileWidthM: number;
  tileHeightM: number;
  pixelsPerMeter: number;
  plankW: number;
  plankL: number;
  seamWidthPx: number;
  seamColor: string;
}

interface ChessboardTileMetrics {
  tileWidthPx: number;
  tileHeightPx: number;
  tileWidthM: number;
  tileHeightM: number;
  pixelsPerMeter: number;
  plankW: number;
  modulePx: number;
  moduleM: number;
  stripCount: number;
}

interface BasketweaveTileMetrics {
  tileWidthPx: number;
  tileHeightPx: number;
  tileWidthM: number;
  tileHeightM: number;
  pixelsPerMeter: number;
  plankW: number;
  modulePx: number;
  moduleM: number;
  stripCount: number;
}

@Component({
  selector: 'frantinco-three-room-canvas',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './three-room-canvas.component.html',
  styleUrl: './three-room-canvas.component.scss'
})
export class ThreeRoomCanvasComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() imageUrl: string | null = null;
  @Input() resultImageUrl: string | null = null;
  @Input() previewTextureUrl: string | null = null;
  @Input() fallbackTextureUrl: string | null = null;
  @Input() maskImageUrl: string | null = null;
  @Input() sliceImageUrl: string | null = null;
  @Input() perspectivePoints: PerspectivePoints | null = null;
  @Input() mode: ViewerMode = 'original';
  @Input() comparePosition = 0.5;
  @Input() resultOpacity = 1;
  @Input() previewOpacity = 0.82;
  @Input() previewProjectiveUv = true;
  @Input() previewUseRawFloorTexture = true;
  @Input() previewUseRealProductScale = true;
  @Input() previewFloorRepeatX = 5;
  @Input() previewRawFloorPlankShader = true;
  @Input() previewPreserveProductAspect = true;
  @Input() previewRawTextureMaxSide = 4096;
  @Input() previewFloorMainRepeatX = 5;
  @Input() previewFloorExtraRepeatX = 2;
  @Input() previewFloorRepeatY = 5;
  @Input() previewFloorPatternRotationDeg = 0;
  @Input() previewFloorUseTileCellTexture = true;
  @Input() previewCeilingRepeatX = 7;
  @Input() previewCeilingRepeatY = 3;
  @Input() previewCeilingExtraSide = 0.35;
  @Input() previewCeilingExtraBack = 0.45;
  @Input() previewCeilingExtraFront = 0.10;
  @Input() previewCeilingUseProjectiveUv = true;
  @Input() previewCeilingUseTileCellTexture = true;
  @Input() previewWallUseProjectiveUv = false;
  @Input() previewWallUsePanelTexture = true;
  @Input() previewWallExtraSide = 0.04;
  @Input() previewWallExtraTop = 0.02;
  @Input() previewWallExtraBottom = 0.02;
  @Input() previewWallRepeatX = 8;
  @Input() previewWallRepeatY = 1;
  @Input() previewWallRotationDeg = 0;
  @Input() showPerspectiveEditor = false;
  @Input() surfaceHotspots: SurfaceHotspot[] = [];
  @Input() activeSurface: TargetSurface = 'floor';
  @Input() activeInstanceId: string | null = null;
  @Input() pattern: SurfacePattern = 'straight';
  @Input() textureScale = 1;
  @Input() textureRotation = 0;
  @Input() textureOffsetX = 0;
  @Input() textureOffsetY = 0;
  @Input() plankWidthM = 0.1524;
  @Input() plankLengthM = 0.9144;
  @Input() groutWidthM = 0.002;
  @Input() groutEnabled = false;
  @Input() groutSizeMm = 10;
  @Input() groutColor = '#CD9A6C';
  @Input() roomWidthM = 4;
  @Input() roomDepthM = 5;
  @Input() pixelsPerMeter = 900;
  @Input() previewLayers: PreviewLayerInput[] = [];
  @Input() isProcessing = false;
  @Input() loadingText = '';

  @Output() comparePositionChange = new EventEmitter<number>();
  @Output() perspectivePointsChange = new EventEmitter<PerspectivePoints>();
  @Output() surfaceHotspotClick = new EventEmitter<SurfaceHotspot>();

  @ViewChild('host', { static: true }) hostRef!: ElementRef<HTMLDivElement>;
  @ViewChild('canvas', { static: true }) private canvasRef!: ElementRef<HTMLCanvasElement>;

  imageWidth = 0;
  imageHeight = 0;
  zoom = 1;
  statusMessage = 'Pilih foto ruangan untuk mulai.';
  screenHotspots: ScreenHotspot[] = [];

  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.OrthographicCamera | null = null;
  private imageGroup: THREE.Group | null = null;
  private baseMesh: THREE.Mesh | null = null;
  private resultMesh: THREE.Mesh | null = null;
  private previewMesh: THREE.Mesh | null = null;
  private sliceMesh: THREE.Mesh | null = null;
  private resultMaterial: THREE.ShaderMaterial | null = null;
  private previewMaterial: THREE.ShaderMaterial | null = null;
  private previewTexture: THREE.Texture | null = null;
  private sourceProductTexture: THREE.Texture | null = null;
  private maskTexture: THREE.Texture | null = null;
  private previewLayerMeshes: THREE.Mesh[] = [];
  private previewLayerMaterials: THREE.ShaderMaterial[] = [];
  private previewLayerTextures: THREE.Texture[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private readonly textureLoader = new THREE.TextureLoader();
  private readonly textureCache = new Map<string, Promise<THREE.Texture>>();
  private readonly cachedTextures = new WeakSet<THREE.Texture>();
  private readonly disposableGeneratedTextures = new WeakSet<THREE.Texture>();
  private currentBuildId = 0;
  private fitScale = 1;
  private pan = { x: 0, y: 0 };
  private activeCorner: CornerKey | null = null;
  private isPanning = false;
  private panStart = { x: 0, y: 0, px: 0, py: 0 };
  private editablePoints: PerspectivePoints | null = null;
  private animationFrameId = 0;
  private isDraggingCompareDivider = false;

  ngAfterViewInit(): void {
    this.initThree();
    this.resizeObserver = new ResizeObserver(() => this.resizeRenderer());
    this.resizeObserver.observe(this.hostRef.nativeElement);
    void this.rebuildScene();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mode'] && this.mode === 'compare') {
      this.resetView();
    }

    if (changes['surfaceHotspots'] || changes['activeSurface'] || changes['activeInstanceId']) {
      this.updateScreenHotspots();
    }

    if (changes['perspectivePoints']) {
      this.editablePoints = this.clonePoints(this.perspectivePoints);
      this.updatePreviewGeometry();
      this.scheduleRender();
    }

    if (!this.renderer) {
      return;
    }

    if (
      changes['imageUrl'] ||
      changes['resultImageUrl'] ||
      changes['previewTextureUrl'] ||
      changes['fallbackTextureUrl'] ||
      changes['maskImageUrl'] ||
      changes['sliceImageUrl'] ||
      changes['previewLayers'] ||
      changes['previewWallUsePanelTexture']
    ) {
      void this.rebuildScene();
      return;
    }

    if (
  changes['pattern'] ||
  changes['plankWidthM'] ||
  changes['plankLengthM'] ||
  changes['groutWidthM'] ||
  changes['groutEnabled'] ||
  changes['groutSizeMm'] ||
  changes['groutColor'] ||
  changes['roomWidthM'] ||
  changes['roomDepthM'] ||
  changes['pixelsPerMeter'] ||
  changes['textureScale'] ||
  changes['textureRotation'] ||
  changes['textureOffsetX'] ||
  changes['textureOffsetY'] ||
  changes['previewWallUseProjectiveUv'] ||
  changes['previewWallExtraSide'] ||
  changes['previewWallExtraTop'] ||
  changes['previewWallExtraBottom'] ||
  changes['previewWallRepeatX'] ||
  changes['previewWallRepeatY'] ||
  changes['previewWallRotationDeg']
) {
  if (this.previewLayers?.length) {
    void this.rebuildScene();
    return;
  }

  void this.rebuildPreviewTextureOnly();

  if (this.previewTexture) {
    this.applyPatternTransform(this.previewTexture);
  }

  this.applyRuntimeUniforms();
  this.scheduleRender();
}

    this.applyRuntimeUniforms();
    this.updatePreviewGeometry();
    this.updateScreenHotspots();
    this.scheduleRender();
  }

  ngOnDestroy(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.resizeObserver?.disconnect();
    this.disposeSceneObjects();
    this.disposeTextureCache();
    this.renderer?.dispose();
  }

  get compareDividerLeft(): number {
    const rect = this.getDisplayedImageRect();
    if (!rect) {
      return 0;
    }
    return rect.left + rect.width * this.comparePosition;
  }

  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }

  zoomIn(): void {
    this.zoom = Math.min(4, this.zoom + 0.15);
    this.applyCameraFit();
  }

  zoomOut(): void {
    this.zoom = Math.max(0.35, this.zoom - 0.15);
    this.applyCameraFit();
  }

  exportCurrentImageDataUrl(): string | null {
    if (!this.renderer || !this.scene || !this.camera || !this.canvasRef?.nativeElement) {
      return null;
    }

    this.renderer.render(this.scene, this.camera);

    try {
      return this.canvasRef.nativeElement.toDataURL('image/jpeg', 1.0);
    } catch (error) {
      console.warn('Gagal mengambil snapshot visualizer:', error);
      return null;
    }
  }

  exportCurrentImageOnlyDataUrl(): string | null {
    if (!this.renderer || !this.scene || !this.camera || !this.canvasRef?.nativeElement || !this.hostRef?.nativeElement) {
      return null;
    }

    const imageRect = this.getDisplayedImageRect();
    if (!imageRect || imageRect.width <= 0 || imageRect.height <= 0) {
      return null;
    }

    this.renderer.render(this.scene, this.camera);

    const sourceCanvas = this.canvasRef.nativeElement;
    const host = this.hostRef.nativeElement;
    const scaleX = sourceCanvas.width / Math.max(1, host.clientWidth);
    const scaleY = sourceCanvas.height / Math.max(1, host.clientHeight);
    const sourceX = Math.max(0, Math.round(imageRect.left * scaleX));
    const sourceY = Math.max(0, Math.round(imageRect.top * scaleY));
    const sourceRight = Math.min(sourceCanvas.width, Math.round((imageRect.left + imageRect.width) * scaleX));
    const sourceBottom = Math.min(sourceCanvas.height, Math.round((imageRect.top + imageRect.height) * scaleY));
    const sourceWidth = sourceRight - sourceX;
    const sourceHeight = sourceBottom - sourceY;

    if (sourceWidth <= 0 || sourceHeight <= 0) {
      return null;
    }

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = sourceWidth;
    outputCanvas.height = sourceHeight;

    const context = outputCanvas.getContext('2d');
    if (!context) {
      return null;
    }

    context.drawImage(sourceCanvas, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, sourceWidth, sourceHeight);

    try {
      return outputCanvas.toDataURL('image/jpeg', 1.0);
    } catch (error) {
      console.warn('Gagal mengambil crop snapshot visualizer:', error);
      return null;
    }
  }

  resetView(): void {
    this.zoom = 1;
    this.pan = { x: 0, y: 0 };
    this.applyCameraFit();
  }

  updateZoomPercent(value: number | string): void {
    const percent = this.clampNumber(Number(value) || 100, 35, 400);
    this.zoom = percent / 100;
    this.applyCameraFit();
  }

  onWheel(event: WheelEvent): void {
    if (this.mode === 'compare') {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.08 : 0.08;
    this.zoom = this.clampNumber(this.zoom + delta, 0.35, 4);
    this.applyCameraFit();
  }

  onCompareHostPointerDown(event: PointerEvent): void {
    if (this.mode !== 'compare' || (!this.resultImageUrl && !this.previewTextureUrl)) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('.surface-hotspot') || target?.closest('.canvas-tools') || target?.closest('.quad-overlay')) {
      return;
    }
    event.preventDefault();
    this.isDraggingCompareDivider = true;
    this.hostRef.nativeElement.setPointerCapture(event.pointerId);
    this.updateComparePositionFromPointer(event);
  }

  onCompareDividerPointerDown(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingCompareDivider = true;
    this.hostRef.nativeElement.setPointerCapture(event.pointerId);
    this.updateComparePositionFromPointer(event);
  }

  onCompareDividerPointerMove(event: PointerEvent): void {
    if (!this.isDraggingCompareDivider) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.updateComparePositionFromPointer(event);
  }

  onCompareDividerPointerUp(event: PointerEvent): void {
    if (!this.isDraggingCompareDivider) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingCompareDivider = false;
    if (this.hostRef.nativeElement.hasPointerCapture(event.pointerId)) {
      this.hostRef.nativeElement.releasePointerCapture(event.pointerId);
    }
  }

  onCanvasPointerDown(event: PointerEvent): void {
    if (this.mode === 'compare' || this.activeCorner || this.showPerspectiveEditor) {
      return;
    }
    this.isPanning = true;
    this.panStart = { x: event.clientX, y: event.clientY, px: this.pan.x, py: this.pan.y };
    this.canvasRef.nativeElement.setPointerCapture(event.pointerId);
  }

  onCanvasPointerMove(event: PointerEvent): void {
    if (this.mode === 'compare' || !this.isPanning) {
      return;
    }
    this.pan = {
      x: this.panStart.px + event.clientX - this.panStart.x,
      y: this.panStart.py - (event.clientY - this.panStart.y)
    };
    this.applyCameraFit();
  }

  onCanvasPointerUp(event: PointerEvent): void {
    if (this.mode === 'compare') {
      return;
    }
    this.isPanning = false;
    if (this.canvasRef.nativeElement.hasPointerCapture(event.pointerId)) {
      this.canvasRef.nativeElement.releasePointerCapture(event.pointerId);
    }
  }

  overlayPointList(): Array<{ key: CornerKey; label: string; x: number; y: number }> {
    const points = this.getEditablePointsOrDefault();
    return ([
      ['topLeft', 'TL'],
      ['topRight', 'TR'],
      ['bottomRight', 'BR'],
      ['bottomLeft', 'BL']
    ] as Array<[CornerKey, string]>).map(([key, label]) => {
      const screen = this.imageToScreen(points[key]);
      return { key, label, x: screen.x, y: screen.y };
    });
  }

  overlayPolygonPoints(): string {
    return this.overlayPointList().map((point) => `${point.x},${point.y}`).join(' ');
  }

  overlaySegments(): Array<{ x1: number; y1: number; x2: number; y2: number }> {
    const points = this.overlayPointList();
    return points.map((point, index) => {
      const next = points[(index + 1) % points.length];
      return { x1: point.x, y1: point.y, x2: next.x, y2: next.y };
    });
  }

  onHandlePointerDown(event: PointerEvent, key: CornerKey): void {
    if (!this.showPerspectiveEditor) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.activeCorner = key;
    (event.currentTarget as Element).setPointerCapture(event.pointerId);
  }

  onOverlayPointerMove(event: PointerEvent): void {
    if (!this.activeCorner) {
      return;
    }
    const point = this.screenToImage(event.clientX, event.clientY);
    const clamped = {
      x: Math.round(this.clampNumber(point.x, 0, this.imageWidth)),
      y: Math.round(this.clampNumber(point.y, 0, this.imageHeight))
    };
    const current = this.getEditablePointsOrDefault();
    this.editablePoints = {
      ...current,
      [this.activeCorner]: clamped
    };
    this.updatePreviewGeometry();
    this.perspectivePointsChange.emit(this.clonePoints(this.editablePoints) as PerspectivePoints);
    this.scheduleRender();
  }

  onOverlayPointerUp(event: PointerEvent): void {
    if ((event.target as Element).hasPointerCapture?.(event.pointerId)) {
      (event.target as Element).releasePointerCapture(event.pointerId);
    }
    this.activeCorner = null;
  }

  useDefaultQuad(): void {
    this.editablePoints = this.defaultQuad();
    this.updatePreviewGeometry();
    this.perspectivePointsChange.emit(this.clonePoints(this.editablePoints) as PerspectivePoints);
    this.scheduleRender();
  }

  onHotspotClick(event: PointerEvent | MouseEvent, hotspot: SurfaceHotspot): void {
    event.preventDefault();
    event.stopPropagation();
    this.surfaceHotspotClick.emit(hotspot);
  }

  onHotspotPointerDown(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onHotspotPointerUp(event: PointerEvent, hotspot: SurfaceHotspot): void {
    event.preventDefault();
    event.stopPropagation();
    this.surfaceHotspotClick.emit(hotspot);
  }

  isHotspotActive(hotspot: SurfaceHotspot): boolean {
    return hotspot.surface === this.activeSurface && (hotspot.instanceId || null) === (this.activeInstanceId || null);
  }

  private initThree(): void {
    const canvas = this.canvasRef.nativeElement;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setClearColor(0xf5f7f4, 1);

    this.scene = new THREE.Scene();
    this.imageGroup = new THREE.Group();
    this.scene.add(this.imageGroup);

    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1000, 1000);
    this.camera.position.set(0, 0, 10);
    this.camera.lookAt(0, 0, 0);
    this.resizeRenderer();
  }

  private async rebuildScene(): Promise<void> {
    const buildId = ++this.currentBuildId;
    this.disposeSceneObjects();
    queueMicrotask(() => {
      this.statusMessage = this.imageUrl ? 'Memuat visualizer...' : 'Pilih foto ruangan untuk mulai.';
    });

    if (!this.renderer || !this.scene || !this.imageGroup || !this.imageUrl) {
      this.scheduleRender();
      return;
    }

    try {
      const baseTexture = await this.loadTexture(this.imageUrl);
      if (buildId !== this.currentBuildId) {
        this.disposeTextureIfDisposable(baseTexture);
        return;
      }
      this.configureTexture(baseTexture);
      const baseSize = this.textureSize(baseTexture);
      this.imageWidth = baseSize.width;
      this.imageHeight = baseSize.height;
      this.editablePoints = this.clonePoints(this.perspectivePoints) ?? this.defaultQuad();

      this.baseMesh = this.createPlaneMesh(baseTexture, this.imageWidth, this.imageHeight, 0, 1);
      this.imageGroup.add(this.baseMesh);

      if (this.resultImageUrl) {
        const resultTexture = await this.loadTexture(this.resultImageUrl);
        if (buildId !== this.currentBuildId) {
          this.disposeTextureIfDisposable(resultTexture);
          return;
        }
        this.configureTexture(resultTexture);
        this.resultMaterial = this.createCompareMaterial(resultTexture);
        this.resultMesh = new THREE.Mesh(new THREE.PlaneGeometry(this.imageWidth, this.imageHeight), this.resultMaterial);
        this.resultMesh.position.z = 1;
        this.resultMesh.renderOrder = 10;
        this.imageGroup.add(this.resultMesh);
      }

      if (this.previewLayers?.length) {
        await this.addPreviewLayerMeshes(buildId, baseTexture);
      } else if (this.previewTextureUrl) {
        this.sourceProductTexture = await this.loadTexture(this.previewTextureUrl, this.fallbackTextureUrl);
        if (buildId !== this.currentBuildId) {
          this.disposeTextureIfDisposable(this.sourceProductTexture);
          return;
        }
        this.configureTexture(this.sourceProductTexture, true);

        this.maskTexture = null;

        if (this.maskImageUrl) {
          try {
            this.maskTexture = await this.loadTexture(this.maskImageUrl);

            if (buildId !== this.currentBuildId) {
              this.disposeTextureIfDisposable(this.maskTexture);
              return;
            }

            this.configureTexture(this.maskTexture);
          } catch (error) {
            console.warn('Mask texture gagal dimuat, preview tetap jalan tanpa mask:', this.maskImageUrl, error);
            this.maskTexture = null;
          }
        }

        await this.rebuildPreviewTextureOnly();

        if (this.previewTexture) {
          this.previewMesh = this.createPreviewMesh(this.previewTexture);
          this.imageGroup.add(this.previewMesh);
        }
      }

      if (this.sliceImageUrl) {
        try {
          const sliceTexture = await this.loadTexture(this.sliceImageUrl);

          if (buildId !== this.currentBuildId) {
            this.disposeTextureIfDisposable(sliceTexture);
            return;
          }

          this.configureTexture(sliceTexture);

          this.sliceMesh = this.createPlaneMesh(
            sliceTexture,
            this.imageWidth,
            this.imageHeight,
            10,
            1
          );

          const sliceMaterial = this.sliceMesh.material as THREE.MeshBasicMaterial;

          sliceMaterial.transparent = true;
          sliceMaterial.opacity = 1;
          sliceMaterial.depthWrite = false;
          sliceMaterial.depthTest = false;
          sliceMaterial.needsUpdate = true;

          this.sliceMesh.position.z = 10;
          this.sliceMesh.renderOrder = 999;

          this.imageGroup.add(this.sliceMesh);
        } catch (error) {
          console.warn('Slice overlay gagal dimuat, preview tetap jalan tanpa slice:', this.sliceImageUrl, error);
          this.sliceMesh = null;
        }
      }

      queueMicrotask(() => {
        this.statusMessage = '';
      });
      this.applyCameraFit();
      this.applyRuntimeUniforms();
    } catch (error) {
      console.error(error);
      queueMicrotask(() => {
        this.statusMessage = 'Gagal memuat gambar. Periksa URL asset atau CORS backend.';
      });
      this.scheduleRender();
    }
  }

  private createPlaneMesh(
    texture: THREE.Texture,
    width: number,
    height: number,
    z: number,
    opacity: number
  ): THREE.Mesh {
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity,
      depthTest: false,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      material
    );

    mesh.position.z = z;
    mesh.renderOrder = z;

    return mesh;
  }

  private createCompareMaterial(texture: THREE.Texture): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      transparent: true,
      extensions: { derivatives: true } as any,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        resultMap: { value: texture },
        compareX: { value: this.comparePosition },
        opacity: { value: this.resultOpacity }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D resultMap;
        uniform float compareX;
        uniform float opacity;
        varying vec2 vUv;
        void main() {
          if (vUv.x > compareX) discard;
          vec4 color = texture2D(resultMap, vUv);
          gl_FragColor = vec4(color.rgb, color.a * opacity);
        }
      `
    });
  }


  private async addPreviewLayerMeshes(buildId: number, baseTexture: THREE.Texture | null = null): Promise<void> {
    for (const layer of this.previewLayers || []) {
      if (!layer.textureUrl || !layer.maskUrl) {
        continue;
      }

      if (this.shouldUsePerspectiveGeometry(layer) && !layer.perspectivePoints) {
        continue;
      }

      try {
        const sourceTexture = await this.loadTexture(layer.textureUrl, this.fallbackTextureUrl);

        if (buildId !== this.currentBuildId) {
          this.disposeTextureIfDisposable(sourceTexture);
          return;
        }

        this.configureTexture(sourceTexture, true);

        const previous = {
          pattern: this.pattern,
          textureScale: this.textureScale,
          textureRotation: this.textureRotation,
          textureOffsetX: this.textureOffsetX,
          textureOffsetY: this.textureOffsetY,
          plankWidthM: this.plankWidthM,
          plankLengthM: this.plankLengthM,
          groutWidthM: this.groutWidthM,
          groutEnabled: this.groutEnabled,
          groutSizeMm: this.groutSizeMm,
          groutColor: this.groutColor,
          roomWidthM: this.roomWidthM,
          roomDepthM: this.roomDepthM,
          pixelsPerMeter: this.pixelsPerMeter
        };

        this.pattern = (layer.pattern || this.pattern) as SurfacePattern;
        this.textureScale = Number(layer.textureScale ?? this.textureScale ?? 1);
        this.textureRotation = Number(layer.rotationAngle ?? this.textureRotation ?? 0);
        this.textureOffsetX = Number(layer.textureOffsetX ?? this.textureOffsetX ?? 0);
        this.textureOffsetY = Number(layer.textureOffsetY ?? this.textureOffsetY ?? 0);
        this.plankWidthM = Number(layer.plankWidthM ?? this.plankWidthM ?? 0.1524);
        this.plankLengthM = Number(layer.plankLengthM ?? this.plankLengthM ?? 0.9144);
        this.groutWidthM = Number(layer.groutWidthM ?? this.groutWidthM ?? 0.002);
        this.groutEnabled = !!(layer.groutEnabled ?? this.groutEnabled);
        this.groutSizeMm = Number(layer.groutSizeMm ?? this.groutSizeMm ?? this.groutWidthM * 1000);
        this.groutColor = String(layer.groutColor || this.groutColor || '#CD9A6C');
        this.roomWidthM = Number(layer.roomWidthM ?? this.roomWidthM ?? 4);
        this.roomDepthM = Number(layer.roomDepthM ?? this.roomDepthM ?? 5);
        this.pixelsPerMeter = Number(layer.pixelsPerMeter ?? this.pixelsPerMeter ?? 900);

        const useDirectRawFloorTexture = this.shouldUseDirectRawFloorTexture(layer);
        let patternTexture: THREE.Texture;
        let patternTextureIsSourceTexture = false;

        if (layer.surface === 'wall' && this.previewWallUsePanelTexture) {
          patternTexture = this.createWallPanelTexture(sourceTexture.image as CanvasImageSource);
          this.disposableGeneratedTextures.add(patternTexture);
        } else if (this.shouldUseCeilingProjectiveLayer(layer) && this.previewCeilingUseTileCellTexture) {
          patternTexture = this.createCeilingTileCellTexture(sourceTexture.image as CanvasImageSource);
          this.disposableGeneratedTextures.add(patternTexture);
        } else if (this.shouldUseFloorTileCellTexture(layer)) {
          patternTexture = this.createFloorTileCellTexture(sourceTexture.image as CanvasImageSource, layer);
          this.disposableGeneratedTextures.add(patternTexture);
        } else if (useDirectRawFloorTexture) {
          patternTexture = sourceTexture;
          patternTextureIsSourceTexture = true;
          this.configureDirectRawFloorTexture(patternTexture);
        } else if (this.shouldUseRawSurfaceTexture(layer)) {
          patternTexture = this.createRawSurfaceTexture(sourceTexture.image as CanvasImageSource);
          this.disposableGeneratedTextures.add(patternTexture);
        } else {
          patternTexture = this.createPatternTexture(sourceTexture.image as CanvasImageSource, layer.surface);
          this.disposableGeneratedTextures.add(patternTexture);
        }

        this.pattern = previous.pattern;
        this.textureScale = previous.textureScale;
        this.textureRotation = previous.textureRotation;
        this.textureOffsetX = previous.textureOffsetX;
        this.textureOffsetY = previous.textureOffsetY;
        this.plankWidthM = previous.plankWidthM;
        this.plankLengthM = previous.plankLengthM;
        this.groutWidthM = previous.groutWidthM;
        this.groutEnabled = previous.groutEnabled;
        this.groutSizeMm = previous.groutSizeMm;
        this.groutColor = previous.groutColor;
        this.roomWidthM = previous.roomWidthM;
        this.roomDepthM = previous.roomDepthM;
        this.pixelsPerMeter = previous.pixelsPerMeter;

        if (!patternTextureIsSourceTexture) {
          this.disposeTextureIfDisposable(sourceTexture);
        }

        const maskTexture = await this.loadTexture(layer.maskUrl);
        let lightTexture: THREE.Texture | null = null;

        if (layer.lightTextureUrl) {
          try {
            lightTexture = await this.loadTexture(layer.lightTextureUrl);
          } catch (lightError) {
            console.warn('Light texture gagal dimuat, preview tetap jalan tanpa light map:', layer.lightTextureUrl, lightError);
            lightTexture = null;
          }
        }

        if (buildId !== this.currentBuildId) {
          this.disposeTextureIfDisposable(patternTexture);
          this.disposeTextureIfDisposable(maskTexture);
          this.disposeTextureIfDisposable(lightTexture);
          return;
        }

        this.configureTexture(maskTexture);
        // Edge mask lebih halus: jangan pakai NearestFilter karena bikin tepi kotak-kotak.
        maskTexture.minFilter = THREE.LinearFilter;
        maskTexture.magFilter = THREE.LinearFilter;
        maskTexture.generateMipmaps = false;
        maskTexture.needsUpdate = true;

        if (lightTexture) {
          this.configureTexture(lightTexture);
          lightTexture.minFilter = THREE.LinearFilter;
          lightTexture.magFilter = THREE.LinearFilter;
          lightTexture.generateMipmaps = false;
          lightTexture.needsUpdate = true;
        }

        const mesh = this.createPreviewLayerMesh(layer, patternTexture, maskTexture, lightTexture, baseTexture);

        this.previewLayerTextures.push(patternTexture, maskTexture);
        if (lightTexture) {
          this.previewLayerTextures.push(lightTexture);
        }
        this.previewLayerMeshes.push(mesh);
        this.previewLayerMaterials.push(mesh.material as THREE.ShaderMaterial);
        this.imageGroup?.add(mesh);
      } catch (error) {
        console.warn('Preview layer gagal dimuat:', layer, error);
      }
    }
  }

  private createPreviewLayerMesh(
    layer: PreviewLayerInput,
    texture: THREE.Texture,
    maskTexture: THREE.Texture,
    lightTexture: THREE.Texture | null = null,
    baseTexture: THREE.Texture | null = null
  ): THREE.Mesh {
    const usePerspectiveGeometry = this.shouldUsePerspectiveGeometry(layer);
    const geometry = this.shouldUseFloorProjectiveLayer(layer) ||
      this.shouldUseCeilingProjectiveLayer(layer) ||
      layer.surface === 'wall'
      ? this.createFullImageGeometry()
      : usePerspectiveGeometry
        ? this.createQuadGeometryForPoints(layer.perspectivePoints || this.defaultQuad())
        : this.createFlatLocalGeometryForLayer(layer);

    const material = this.createPreviewLayerMaterial(layer, texture, maskTexture, lightTexture, baseTexture);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.z = 2 + this.previewLayerMeshes.length * 0.01;
    mesh.renderOrder = 20 + this.previewLayerMeshes.length;
    return mesh;
  }

  private shouldUsePerspectiveGeometry(layer: PreviewLayerInput): boolean {
    // Floor/countertop butuh quad projection.
    // Wall template lebih stabil pakai bbox + full mask clip, karena quad wall
    // sering hanya garis bidang sementara mask instance jauh lebih lengkap.
    // Ceiling template juga lebih stabil flat-local supaya arah pattern konsisten.
    return (
      layer.surface === 'floor' ||
      layer.surface === 'countertop'
    ) && !!layer.perspectivePoints;
  }

  private shouldUseFloorProjectiveLayer(layer: PreviewLayerInput): boolean {
    return layer.surface === 'floor' || layer.surface === 'countertop';
  }

  private shouldUseCeilingProjectiveLayer(layer: PreviewLayerInput): boolean {
    return layer.surface === 'ceiling' && !!this.previewCeilingUseProjectiveUv;
  }

  private shouldUseWallProjectiveLayer(layer: PreviewLayerInput): boolean {
    return false;
  }

  private createFullImageGeometry(): THREE.BufferGeometry {
    const tl = this.imagePointToWorld({ x: 0, y: 0 });
    const tr = this.imagePointToWorld({ x: this.imageWidth, y: 0 });
    const br = this.imagePointToWorld({ x: this.imageWidth, y: this.imageHeight });
    const bl = this.imagePointToWorld({ x: 0, y: this.imageHeight });

    const vertices = new Float32Array([
      tl.x, tl.y, 0,
      tr.x, tr.y, 0,
      br.x, br.y, 0,
      bl.x, bl.y, 0
    ]);

    const uvs = new Float32Array([
      0, 1,
      1, 1,
      1, 0,
      0, 0
    ]);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.computeVertexNormals();
    return geometry;
  }

  private createFlatLocalGeometryForLayer(layer: PreviewLayerInput): THREE.BufferGeometry {
    const bbox = this.displayBBoxForLayer(layer, this.normalizedLayerBBox(layer.bbox))
      || this.bboxFromPerspectivePoints(layer.perspectivePoints)
      || { x1: 0, y1: 0, x2: this.imageWidth, y2: this.imageHeight };

    const tl = this.imagePointToWorld({ x: bbox.x1, y: bbox.y1 });
    const tr = this.imagePointToWorld({ x: bbox.x2, y: bbox.y1 });
    const br = this.imagePointToWorld({ x: bbox.x2, y: bbox.y2 });
    const bl = this.imagePointToWorld({ x: bbox.x1, y: bbox.y2 });

    const vertices = new Float32Array([
      tl.x, tl.y, 0,
      tr.x, tr.y, 0,
      br.x, br.y, 0,
      bl.x, bl.y, 0
    ]);

    // UV lokal per bbox. Ini yang bikin dinding seperti contoh Python:
    // texture di-tile per bidang, bukan per full image dan bukan warp perspektif ekstrem.
    const uvs = new Float32Array([
      0, 1,
      1, 1,
      1, 0,
      0, 0
    ]);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.computeVertexNormals();

    return geometry;
  }

  private displayBBoxForLayer(
    layer: PreviewLayerInput,
    bbox: { x1: number; y1: number; x2: number; y2: number } | null
  ): { x1: number; y1: number; x2: number; y2: number } | null {
    if (!bbox || layer.surface !== 'wall') {
      return bbox;
    }

    const width = bbox.x2 - bbox.x1;
    const height = bbox.y2 - bbox.y1;

    if (height <= width * 1.35) {
      return bbox;
    }

    const lift = 0;

    return {
      x1: bbox.x1,
      x2: bbox.x2,
      y1: this.clampNumber(bbox.y1 - lift, 0, this.imageHeight),
      y2: this.clampNumber(bbox.y2 - lift, 0, this.imageHeight),
    };
  }

  private normalizedLayerBBox(bbox: LayerBBox | null | undefined): { x1: number; y1: number; x2: number; y2: number } | null {
    if (!bbox || this.imageWidth <= 0 || this.imageHeight <= 0) {
      return null;
    }

    const rawX1 = Number(bbox.x1 ?? bbox.x ?? 0);
    const rawY1 = Number(bbox.y1 ?? bbox.y ?? 0);
    const rawX2 = Number.isFinite(Number(bbox.x2))
      ? Number(bbox.x2)
      : rawX1 + Number(bbox.width ?? 0);
    const rawY2 = Number.isFinite(Number(bbox.y2))
      ? Number(bbox.y2)
      : rawY1 + Number(bbox.height ?? 0);

    if (![rawX1, rawY1, rawX2, rawY2].every(Number.isFinite)) {
      return null;
    }

    const x1 = this.clampNumber(Math.min(rawX1, rawX2), 0, this.imageWidth);
    const y1 = this.clampNumber(Math.min(rawY1, rawY2), 0, this.imageHeight);
    const x2 = this.clampNumber(Math.max(rawX1, rawX2), 0, this.imageWidth);
    const y2 = this.clampNumber(Math.max(rawY1, rawY2), 0, this.imageHeight);

    if (x2 - x1 < 2 || y2 - y1 < 2) {
      return null;
    }

    return { x1, y1, x2, y2 };
  }

  private bboxFromPerspectivePoints(points: PerspectivePoints | null | undefined): { x1: number; y1: number; x2: number; y2: number } | null {
    if (!points || this.imageWidth <= 0 || this.imageHeight <= 0) {
      return null;
    }

    const xs = [points.topLeft.x, points.topRight.x, points.bottomRight.x, points.bottomLeft.x].map(Number);
    const ys = [points.topLeft.y, points.topRight.y, points.bottomRight.y, points.bottomLeft.y].map(Number);

    if (![...xs, ...ys].every(Number.isFinite)) {
      return null;
    }

    return {
      x1: this.clampNumber(Math.min(...xs), 0, this.imageWidth),
      y1: this.clampNumber(Math.min(...ys), 0, this.imageHeight),
      x2: this.clampNumber(Math.max(...xs), 0, this.imageWidth),
      y2: this.clampNumber(Math.max(...ys), 0, this.imageHeight)
    };
  }

  private wallBboxUvForLayer(layer: PreviewLayerInput): { min: THREE.Vector2; size: THREE.Vector2 } {
    const bbox = this.displayBBoxForLayer(layer, this.normalizedLayerBBox(layer.bbox))
      || this.bboxFromPerspectivePoints(layer.perspectivePoints)
      || this.bboxFromPerspectivePoints(this.wallQuadFromPolygon(layer.polygon || null))
      || { x1: 0, y1: 0, x2: this.imageWidth, y2: this.imageHeight };

    if (this.imageWidth <= 0 || this.imageHeight <= 0) {
      return {
        min: new THREE.Vector2(0, 0),
        size: new THREE.Vector2(1, 1)
      };
    }

    const width = Math.max(1, bbox.x2 - bbox.x1);
    const height = Math.max(1, bbox.y2 - bbox.y1);

    return {
      min: new THREE.Vector2(bbox.x1 / this.imageWidth, bbox.y1 / this.imageHeight),
      size: new THREE.Vector2(width / this.imageWidth, height / this.imageHeight)
    };
  }

  private shouldUseProjectiveUvForLayer(layer: PreviewLayerInput): boolean {
    return !!this.previewProjectiveUv && (
      this.shouldUseFloorProjectiveLayer(layer) ||
      this.shouldUseCeilingProjectiveLayer(layer) ||
      this.shouldUseWallProjectiveLayer(layer) ||
      this.shouldUsePerspectiveGeometry(layer)
    );
  }

  private projectiveUvMatrixForLayer(layer: PreviewLayerInput): THREE.Matrix3 {
    if (!this.shouldUseProjectiveUvForLayer(layer) || this.imageWidth <= 0 || this.imageHeight <= 0) {
      return new THREE.Matrix3().identity();
    }

    let points: PerspectivePoints;

    if (this.shouldUseFloorProjectiveLayer(layer)) {
      points = this.expandedFloorQuadForLayer(layer);
    } else if (this.shouldUseCeilingProjectiveLayer(layer)) {
      points = this.expandedCeilingQuadForLayer(layer);
    } else if (this.shouldUseWallProjectiveLayer(layer)) {
      points = this.expandedWallQuadForLayer(layer);
    } else {
      points = layer.perspectivePoints || this.defaultQuad();
    }

    const source = [
      { x: points.topLeft.x / this.imageWidth, y: points.topLeft.y / this.imageHeight },
      { x: points.topRight.x / this.imageWidth, y: points.topRight.y / this.imageHeight },
      { x: points.bottomRight.x / this.imageWidth, y: points.bottomRight.y / this.imageHeight },
      { x: points.bottomLeft.x / this.imageWidth, y: points.bottomLeft.y / this.imageHeight }
    ];

    const target = [
      { x: 0, y: 1 },
      { x: 1, y: 1 },
      { x: 1, y: 0 },
      { x: 0, y: 0 }
    ];

    return this.homographyMatrix(source, target);
  }

  private floorPerspectivePointsForLayer(layer: PreviewLayerInput): PerspectivePoints {
    const polygonQuad = this.floorQuadFromPolygon(layer.polygon || null);

    if (polygonQuad) {
      return this.visualFloorQuadFromBase(polygonQuad);
    }

    if (layer.perspectivePoints) {
      return this.visualFloorQuadFromBase(layer.perspectivePoints);
    }

    return this.visualFloorQuadFromBase(this.defaultQuad());
  }

  private visualFloorQuadFromBase(base: PerspectivePoints): PerspectivePoints {
    const topInsetRatio = 0.18;
    const bottomExpandRatio = 0.10;

    const tl = new THREE.Vector2(base.topLeft.x, base.topLeft.y);
    const tr = new THREE.Vector2(base.topRight.x, base.topRight.y);
    const br = new THREE.Vector2(base.bottomRight.x, base.bottomRight.y);
    const bl = new THREE.Vector2(base.bottomLeft.x, base.bottomLeft.y);

    const topStep = tr.clone().sub(tl).multiplyScalar(topInsetRatio);
    const bottomStep = br.clone().sub(bl).multiplyScalar(bottomExpandRatio);

    return {
      topLeft: {
        x: tl.x + topStep.x,
        y: tl.y + topStep.y
      },
      topRight: {
        x: tr.x - topStep.x,
        y: tr.y - topStep.y
      },
      bottomRight: {
        x: br.x + bottomStep.x,
        y: br.y + bottomStep.y
      },
      bottomLeft: {
        x: bl.x - bottomStep.x,
        y: bl.y - bottomStep.y
      }
    };
  }

  private floorQuadFromPolygon(polygon: Point2D[] | null | undefined): PerspectivePoints | null {
    if (!polygon || polygon.length < 4) {
      return null;
    }

    const points = polygon
      .map((point) => ({ x: Number(point.x), y: Number(point.y) }))
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

    if (points.length < 4) {
      return null;
    }

    const maxY = Math.max(...points.map((point) => point.y));
    const minY = Math.min(...points.map((point) => point.y));
    const frontPoints = points.filter((point) => point.y >= maxY - 12);
    const backPoints = points.filter((point) => point.y <= minY + 24);

    if (!frontPoints.length || !backPoints.length) {
      return null;
    }

    const bottomLeft = frontPoints.reduce((current, next) => (next.x < current.x ? next : current), frontPoints[0]);
    const bottomRight = frontPoints.reduce((current, next) => (next.x > current.x ? next : current), frontPoints[0]);
    const topLeft = backPoints.reduce((current, next) => (next.x < current.x ? next : current), backPoints[0]);
    const topRight = backPoints.reduce((current, next) => (next.x > current.x ? next : current), backPoints[0]);

    return {
      topLeft,
      topRight,
      bottomRight,
      bottomLeft
    };
  }

  private expandedFloorQuadForLayer(layer: PreviewLayerInput): PerspectivePoints {
    const base = this.floorPerspectivePointsForLayer(layer);
    const mainRepeatX = Math.max(1, Number(this.previewFloorMainRepeatX || 5));
    const extraEachSide = Math.max(0, Number(this.previewFloorExtraRepeatX || 2));

    const tl = new THREE.Vector2(base.topLeft.x, base.topLeft.y);
    const tr = new THREE.Vector2(base.topRight.x, base.topRight.y);
    const br = new THREE.Vector2(base.bottomRight.x, base.bottomRight.y);
    const bl = new THREE.Vector2(base.bottomLeft.x, base.bottomLeft.y);

    const topStep = tr.clone().sub(tl).divideScalar(mainRepeatX);
    const bottomStep = br.clone().sub(bl).divideScalar(mainRepeatX);

    return {
      topLeft: {
        x: tl.x - topStep.x * extraEachSide,
        y: tl.y - topStep.y * extraEachSide
      },
      topRight: {
        x: tr.x + topStep.x * extraEachSide,
        y: tr.y + topStep.y * extraEachSide
      },
      bottomRight: {
        x: br.x + bottomStep.x * extraEachSide,
        y: br.y + bottomStep.y * extraEachSide
      },
      bottomLeft: {
        x: bl.x - bottomStep.x * extraEachSide,
        y: bl.y - bottomStep.y * extraEachSide
      }
    };
  }

  private ceilingPerspectivePointsForLayer(layer: PreviewLayerInput): PerspectivePoints {
    if (layer.perspectivePoints) {
      return layer.perspectivePoints;
    }

    const polygonQuad = this.ceilingQuadFromPolygon(layer.polygon || null);
    if (polygonQuad) {
      return polygonQuad;
    }

    return this.defaultQuad();
  }

  private ceilingQuadFromPolygon(polygon: Point2D[] | null | undefined): PerspectivePoints | null {
    if (!polygon || polygon.length < 4) {
      return null;
    }

    const points = polygon
      .map((point) => ({ x: Number(point.x), y: Number(point.y) }))
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

    if (points.length < 4) {
      return null;
    }

    const minY = Math.min(...points.map((point) => point.y));
    const maxY = Math.max(...points.map((point) => point.y));
    const backPoints = points.filter((point) => point.y <= minY + 24);
    const frontPoints = points.filter((point) => point.y >= maxY - 24);

    if (!backPoints.length || !frontPoints.length) {
      return null;
    }

    const topLeft = backPoints.reduce((current, next) => (next.x < current.x ? next : current), backPoints[0]);
    const topRight = backPoints.reduce((current, next) => (next.x > current.x ? next : current), backPoints[0]);
    const bottomLeft = frontPoints.reduce((current, next) => (next.x < current.x ? next : current), frontPoints[0]);
    const bottomRight = frontPoints.reduce((current, next) => (next.x > current.x ? next : current), frontPoints[0]);

    return {
      topLeft,
      topRight,
      bottomRight,
      bottomLeft
    };
  }

  private expandedCeilingQuadForLayer(layer: PreviewLayerInput): PerspectivePoints {
    const base = this.ceilingPerspectivePointsForLayer(layer);

    const tl = new THREE.Vector2(base.topLeft.x, base.topLeft.y);
    const tr = new THREE.Vector2(base.topRight.x, base.topRight.y);
    const br = new THREE.Vector2(base.bottomRight.x, base.bottomRight.y);
    const bl = new THREE.Vector2(base.bottomLeft.x, base.bottomLeft.y);

    const topVec = tr.clone().sub(tl);
    const frontVec = br.clone().sub(bl);
    const depthLeft = tl.clone().sub(bl);
    const depthRight = tr.clone().sub(br);

    const sideExtra = Math.max(0, Number(this.previewCeilingExtraSide ?? 0.35));
    const backExtra = Math.max(0, Number(this.previewCeilingExtraBack ?? 0.45));
    const frontExtra = Math.max(0, Number(this.previewCeilingExtraFront ?? 0.10));

    return {
      topLeft: {
        x: tl.x - topVec.x * sideExtra + depthLeft.x * backExtra,
        y: tl.y - topVec.y * sideExtra + depthLeft.y * backExtra
      },
      topRight: {
        x: tr.x + topVec.x * sideExtra + depthRight.x * backExtra,
        y: tr.y + topVec.y * sideExtra + depthRight.y * backExtra
      },
      bottomRight: {
        x: br.x + frontVec.x * sideExtra - depthRight.x * frontExtra,
        y: br.y + frontVec.y * sideExtra - depthRight.y * frontExtra
      },
      bottomLeft: {
        x: bl.x - frontVec.x * sideExtra - depthLeft.x * frontExtra,
        y: bl.y - frontVec.y * sideExtra - depthLeft.y * frontExtra
      }
    };
  }

  private wallPerspectivePointsForLayer(layer: PreviewLayerInput): PerspectivePoints {
    if (layer.perspectivePoints) {
      return layer.perspectivePoints;
    }

    const polygonQuad = this.wallQuadFromPolygon(layer.polygon || null);
    if (polygonQuad) {
      return polygonQuad;
    }

    return this.defaultQuad();
  }

  private wallQuadFromPolygon(polygon: Point2D[] | null | undefined): PerspectivePoints | null {
    if (!polygon || polygon.length < 4) {
      return null;
    }

    const points = polygon
      .map((point) => ({ x: Number(point.x), y: Number(point.y) }))
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

    if (points.length < 4) {
      return null;
    }

    const minY = Math.min(...points.map((point) => point.y));
    const maxY = Math.max(...points.map((point) => point.y));
    const topPoints = points.filter((point) => point.y <= minY + 28);
    const bottomPoints = points.filter((point) => point.y >= maxY - 28);

    if (!topPoints.length || !bottomPoints.length) {
      return null;
    }

    const topLeft = topPoints.reduce((current, next) => (next.x < current.x ? next : current), topPoints[0]);
    const topRight = topPoints.reduce((current, next) => (next.x > current.x ? next : current), topPoints[0]);
    const bottomLeft = bottomPoints.reduce((current, next) => (next.x < current.x ? next : current), bottomPoints[0]);
    const bottomRight = bottomPoints.reduce((current, next) => (next.x > current.x ? next : current), bottomPoints[0]);

    return {
      topLeft,
      topRight,
      bottomRight,
      bottomLeft
    };
  }

  private expandedWallQuadForLayer(layer: PreviewLayerInput): PerspectivePoints {
    const base = this.wallPerspectivePointsForLayer(layer);

    const tl = new THREE.Vector2(base.topLeft.x, base.topLeft.y);
    const tr = new THREE.Vector2(base.topRight.x, base.topRight.y);
    const br = new THREE.Vector2(base.bottomRight.x, base.bottomRight.y);
    const bl = new THREE.Vector2(base.bottomLeft.x, base.bottomLeft.y);

    const topVec = tr.clone().sub(tl);
    const bottomVec = br.clone().sub(bl);
    const leftVec = bl.clone().sub(tl);
    const rightVec = br.clone().sub(tr);

    const sideExtra = Math.max(0, Number(this.previewWallExtraSide ?? 0.04));
    const topExtra = Math.max(0, Number(this.previewWallExtraTop ?? 0.02));
    const bottomExtra = Math.max(0, Number(this.previewWallExtraBottom ?? 0.02));

    return {
      topLeft: {
        x: tl.x - topVec.x * sideExtra - leftVec.x * topExtra,
        y: tl.y - topVec.y * sideExtra - leftVec.y * topExtra
      },
      topRight: {
        x: tr.x + topVec.x * sideExtra - rightVec.x * topExtra,
        y: tr.y + topVec.y * sideExtra - rightVec.y * topExtra
      },
      bottomRight: {
        x: br.x + bottomVec.x * sideExtra + rightVec.x * bottomExtra,
        y: br.y + bottomVec.y * sideExtra + rightVec.y * bottomExtra
      },
      bottomLeft: {
        x: bl.x - bottomVec.x * sideExtra + leftVec.x * bottomExtra,
        y: bl.y - bottomVec.y * sideExtra + leftVec.y * bottomExtra
      }
    };
  }

  private homographyMatrix(
    source: Array<{ x: number; y: number }>,
    target: Array<{ x: number; y: number }>
  ): THREE.Matrix3 {
    const a: number[][] = [];
    const b: number[] = [];

    for (let i = 0; i < 4; i += 1) {
      const x = source[i].x;
      const y = source[i].y;
      const u = target[i].x;
      const v = target[i].y;

      a.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
      b.push(u);

      a.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
      b.push(v);
    }

    const h = this.solveLinearSystem(a, b);
    if (!h) {
      return new THREE.Matrix3().identity();
    }

    return new THREE.Matrix3().set(
      h[0], h[1], h[2],
      h[3], h[4], h[5],
      h[6], h[7], 1
    );
  }

  private solveLinearSystem(a: number[][], b: number[]): number[] | null {
    const n = b.length;
    const m = a.map((row, index) => [...row, b[index]]);

    for (let col = 0; col < n; col += 1) {
      let pivot = col;
      for (let row = col + 1; row < n; row += 1) {
        if (Math.abs(m[row][col]) > Math.abs(m[pivot][col])) {
          pivot = row;
        }
      }

      if (Math.abs(m[pivot][col]) < 1e-10) {
        return null;
      }

      if (pivot !== col) {
        [m[col], m[pivot]] = [m[pivot], m[col]];
      }

      const div = m[col][col];
      for (let k = col; k <= n; k += 1) {
        m[col][k] /= div;
      }

      for (let row = 0; row < n; row += 1) {
        if (row === col) {
          continue;
        }

        const factor = m[row][col];
        for (let k = col; k <= n; k += 1) {
          m[row][k] -= factor * m[col][k];
        }
      }
    }

    return m.map((row) => row[n]);
  }

  private groutEnabledForLayer(layer: PreviewLayerInput): boolean {
    const pattern = String(layer.pattern || this.pattern || 'straight').toLowerCase();
    if (pattern === 'herringbone' || pattern === 'chevron' || pattern === 'brick' || pattern === 'brick_1_3' || pattern === 'mix' || pattern === 'grid' || pattern === 'chessboard' || pattern === 'basketweave' || pattern === 'basket' || pattern === 'offset' || pattern === 'third') {
      return false;
    }

    const legacySizeMm = Number(layer.groutWidthM ?? this.groutWidthM ?? 0) * 1000;
    const sizeMm = Number(layer.groutSizeMm ?? this.groutSizeMm ?? legacySizeMm);
    return !!(layer.groutEnabled ?? this.groutEnabled) && sizeMm > 0;
  }

  private groutSizeMmForLayer(layer: PreviewLayerInput): number {
    const legacySizeMm = Number(layer.groutWidthM ?? this.groutWidthM ?? 0) * 1000;
    return this.clampNumber(Number(layer.groutSizeMm ?? this.groutSizeMm ?? legacySizeMm ?? 0), 0, 80);
  }

  private groutColorForLayer(layer: PreviewLayerInput): THREE.Color {
    return new THREE.Color(String(layer.groutColor || this.groutColor || '#CD9A6C'));
  }

  private groutLineSizeForLayer(layer: PreviewLayerInput): THREE.Vector2 {
    const groutSizeMm = this.groutSizeMmForLayer(layer);

    if (!this.groutEnabledForLayer(layer) || groutSizeMm <= 0) {
      return new THREE.Vector2(0, 0);
    }

    const repeatScaleVec = this.previewRepeatScaleVecForLayer(layer);
    const roomWidthMm = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30) * 1000;
    const roomDepthMm = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40) * 1000;
    const surfaceWidthMm = Number(layer.surfaceWidthMm || 0) || roomWidthMm;
    const surfaceVerticalMm =
      Number(layer.surfaceLengthMm || layer.surfaceDepthMm || 0) ||
      Number(layer.surfaceHeightMm || 0) ||
      roomDepthMm;

    if (surfaceWidthMm > 0 && surfaceVerticalMm > 0) {
      const visualTileWidthMm = surfaceWidthMm / Math.max(repeatScaleVec.x, 0.0001);
      const visualTileHeightMm = surfaceVerticalMm / Math.max(repeatScaleVec.y, 0.0001);

      return new THREE.Vector2(
        this.clampNumber(groutSizeMm / Math.max(visualTileWidthMm, 1), 0, 0.08),
        this.clampNumber(groutSizeMm / Math.max(visualTileHeightMm, 1), 0, 0.08)
      );
    }

    const productWidthMm =
      Number(layer.productWidthMm || 0) ||
      Number(layer.plankWidthM || this.plankWidthM || 0.1524) * 1000 ||
      300;
    const productHeightMm =
      Number(layer.productHeightMm || 0) ||
      Number(layer.plankLengthM || this.plankLengthM || 0.9144) * 1000 ||
      productWidthMm;

    return new THREE.Vector2(
      this.clampNumber(groutSizeMm / Math.max(productWidthMm, 1), 0, 0.08),
      this.clampNumber(groutSizeMm / Math.max(productHeightMm, 1), 0, 0.08)
    );
  }

  private createPreviewLayerMaterial(
    layer: PreviewLayerInput,
    texture: THREE.Texture,
    maskTexture: THREE.Texture,
    lightTexture: THREE.Texture | null = null,
    baseTexture: THREE.Texture | null = null
  ): THREE.ShaderMaterial {
    const repeatScaleVec = this.previewRepeatScaleVecForLayer(layer);
    const rotationRad = this.patternRotationRadForLayer(layer);
    const offset = new THREE.Vector2(
      Number(layer.textureOffsetX ?? this.textureOffsetX ?? 0),
      Number(layer.textureOffsetY ?? this.textureOffsetY ?? 0)
    );
    const wallBbox = this.wallBboxUvForLayer(layer);
    const groutLineSize = this.groutLineSizeForLayer(layer);
    const groutColor = this.groutColorForLayer(layer);

    return new THREE.ShaderMaterial({
      transparent: true,
      extensions: { derivatives: true } as any,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: {
        patternMap: { value: texture },
        maskMap: { value: maskTexture },
        lightMap: { value: lightTexture },
        baseMap: { value: baseTexture },
        hasMask: { value: 1 },
        hasLight: { value: 0 },
        hasBase: { value: 0 },
        baseBlend: { value: this.previewBaseBlendForLayer(layer) },
        textureFlipY: { value: this.previewTextureFlipYForLayer(layer) ? 1 : 0 },
        maskFlipY: { value: layer.maskFlipY === false ? 0 : 1 },
        opacity: { value: Number(layer.opacity ?? this.previewOpacity ?? 0.82) },
        imageWidth: { value: this.imageWidth },
        imageHeight: { value: this.imageHeight },
        repeatScaleVec: { value: repeatScaleVec },
        gridPhysicalRotation: { value: this.gridPhysicalRotationEnabledForLayer(layer) ? 1 : 0 },
        floorSurfaceSizeM: { value: this.floorSurfaceSizeVecForLayer(layer) },
        rawFloorPlankMode: { value: this.shouldUseRawFloorPlankShaderForLayer(layer) ? 1 : 0 },
        useProjectiveUv: { value: this.shouldUseProjectiveUvForLayer(layer) ? 1 : 0 },
        projectiveUvMatrix: { value: this.projectiveUvMatrixForLayer(layer) },
        rotationRad: { value: rotationRad },
        offset: { value: offset },
        wallBboxMin: { value: wallBbox.min },
        wallBboxSize: { value: wallBbox.size },
        useWallMaskUv: { value: layer.surface === 'wall' ? 1 : 0 },
        groutEnabled: { value: this.groutEnabledForLayer(layer) ? 1 : 0 },
        groutLineSize: { value: groutLineSize },
        groutColor: { value: groutColor },
        compareX: { value: this.clampNumber(this.comparePosition, 0, 1) },
        isCompare: { value: this.mode === 'compare' ? 1 : 0 }
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec2 vImageUv;

        uniform float imageWidth;
        uniform float imageHeight;

        void main() {
          vUv = uv;

          vImageUv = vec2(
            (position.x + imageWidth * 0.5) / imageWidth,
            (imageHeight * 0.5 - position.y) / imageHeight
          );

          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D patternMap;
        uniform sampler2D maskMap;
        uniform sampler2D lightMap;
        uniform sampler2D baseMap;
        uniform float hasMask;
        uniform float hasLight;
        uniform float hasBase;
        uniform float baseBlend;
        uniform float textureFlipY;
        uniform float maskFlipY;
        uniform float opacity;
        uniform vec2 repeatScaleVec;
        uniform float gridPhysicalRotation;
        uniform vec2 floorSurfaceSizeM;
        uniform float rawFloorPlankMode;
        uniform float useProjectiveUv;
        uniform mat3 projectiveUvMatrix;
        uniform float rotationRad;
        uniform vec2 offset;
        uniform vec2 wallBboxMin;
        uniform vec2 wallBboxSize;
        uniform float useWallMaskUv;
        uniform float groutEnabled;
        uniform vec2 groutLineSize;
        uniform vec3 groutColor;
        uniform float compareX;
        uniform float isCompare;
        uniform float imageWidth;
        uniform float imageHeight;

        varying vec2 vUv;
        varying vec2 vImageUv;

        vec2 projectiveFloorUv(vec2 imageUv, vec2 fallbackUv) {
          if (useProjectiveUv < 0.5) {
            return fallbackUv;
          }

          vec3 projected = projectiveUvMatrix * vec3(imageUv, 1.0);
          float w = abs(projected.z) < 0.00001 ? 0.00001 : projected.z;
          return projected.xy / w;
        }

        vec2 rotateUv(vec2 uv, float angle) {
          vec2 centered = uv - vec2(0.5);
          float c = cos(angle);
          float s = sin(angle);
          mat2 rot = mat2(c, -s, s, c);
          return rot * centered + vec2(0.5);
        }

        vec2 rotateUvAspect(vec2 uv, float angleRad, vec2 aspect) {
          vec2 center = vec2(0.5);
          vec2 p = uv - center;
          p.x *= aspect.x;
          p.y *= aspect.y;

          float c = cos(angleRad);
          float s = sin(angleRad);
          vec2 r = vec2(
            p.x * c - p.y * s,
            p.x * s + p.y * c
          );

          r.x /= aspect.x;
          r.y /= aspect.y;
          return r + center;
        }

        float maskSample(vec2 uv) {
          vec4 color = texture2D(maskMap, uv);
          float luma = max(color.r, max(color.g, color.b));
          return luma * color.a;
        }

        float hash12(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        vec4 sampleRawFloorPlankTexture(vec2 tiledUv) {
          vec2 cell = floor(tiledUv);
          vec2 local = fract(tiledUv);

          float r = hash12(cell);
          local.y = fract(local.y + r * 0.72);
          if (r > 0.55) {
            local.x = 1.0 - local.x;
          }

          vec4 color = texture2D(patternMap, local);
          return color;
        }

        float groutLineAlpha(vec2 tiledUv) {
          if (groutEnabled < 0.5 || max(groutLineSize.x, groutLineSize.y) <= 0.0) {
            return 0.0;
          }

          vec2 gap = clamp(groutLineSize, vec2(0.0), vec2(0.45));
          vec2 period = vec2(1.0) + gap;
          vec2 local = mod(tiledUv, period);
          vec2 aa = max(fwidth(tiledUv) * 1.10, vec2(0.00035));
          float vertical = smoothstep(1.0 - aa.x, 1.0 + aa.x, local.x) *
            (1.0 - smoothstep(period.x - aa.x, period.x, local.x));
          float horizontal = smoothstep(1.0 - aa.y, 1.0 + aa.y, local.y) *
            (1.0 - smoothstep(period.y - aa.y, period.y, local.y));
          return clamp(max(vertical, horizontal), 0.0, 1.0);
        }

        vec2 groutProductSampleUv(vec2 tiledUv) {
          if (groutEnabled < 0.5 || max(groutLineSize.x, groutLineSize.y) <= 0.0) {
            return tiledUv;
          }

          vec2 gap = clamp(groutLineSize, vec2(0.0), vec2(0.45));
          vec2 period = vec2(1.0) + gap;
          vec2 cell = floor(tiledUv / period);
          vec2 local = mod(tiledUv, period);
          return cell + clamp(local, vec2(0.0), vec2(0.9999));
        }

        float sampleMaskAlpha9(vec2 uv) {
          vec2 px = vec2(
            1.0 / max(imageWidth, 1.0),
            1.0 / max(imageHeight, 1.0)
          );

          float total = 0.0;
          total += maskSample(uv + px * vec2(-1.0, -1.0)) * 1.0;
          total += maskSample(uv + px * vec2( 0.0, -1.0)) * 2.0;
          total += maskSample(uv + px * vec2( 1.0, -1.0)) * 1.0;

          total += maskSample(uv + px * vec2(-1.0,  0.0)) * 2.0;
          total += maskSample(uv) * 4.0;
          total += maskSample(uv + px * vec2( 1.0,  0.0)) * 2.0;

          total += maskSample(uv + px * vec2(-1.0,  1.0)) * 1.0;
          total += maskSample(uv + px * vec2( 0.0,  1.0)) * 2.0;
          total += maskSample(uv + px * vec2( 1.0,  1.0)) * 1.0;

          return total / 16.0;
        }

        void main() {
          if (isCompare > 0.5 && vImageUv.x > compareX) {
            discard;
          }

          vec2 surfaceUv = projectiveFloorUv(vImageUv, vUv);
          vec2 patternUv;

          if (useWallMaskUv > 0.5) {
            surfaceUv = (vImageUv - wallBboxMin) / max(wallBboxSize, vec2(0.0001));
            surfaceUv = clamp(surfaceUv, 0.0, 1.0);

            vec2 aspect = vec2(
              max(wallBboxSize.x, 0.0001),
              max(wallBboxSize.y, 0.0001)
            );
            surfaceUv = rotateUvAspect(surfaceUv, rotationRad, aspect);
            patternUv = surfaceUv * repeatScaleVec + offset;
          } else {
            if (gridPhysicalRotation > 0.5) {
              vec2 surfaceOffset = offset / max(repeatScaleVec, vec2(0.0001));
              surfaceUv = rotateUvAspect(surfaceUv + surfaceOffset, rotationRad, floorSurfaceSizeM);
              patternUv = surfaceUv * repeatScaleVec;
            } else {
              patternUv = surfaceUv * repeatScaleVec + offset;
              vec2 center = repeatScaleVec * 0.5;
              vec2 p = patternUv - center;
              float c = cos(rotationRad);
              float s = sin(rotationRad);
              patternUv = vec2(
                p.x * c - p.y * s,
                p.x * s + p.y * c
              ) + center;
            }
          }

          if (textureFlipY > 0.5) {
            patternUv.y = 1.0 - patternUv.y;
          }

          vec2 textureUv = groutProductSampleUv(patternUv);
          vec4 patternColor = texture2D(patternMap, textureUv);
          float groutAlpha = groutLineAlpha(patternUv);
          patternColor.rgb = mix(patternColor.rgb, groutColor, groutAlpha);

          if (useWallMaskUv > 0.5) {
            vec2 wallClipUv = maskFlipY > 0.5
              ? vec2(vImageUv.x, 1.0 - vImageUv.y)
              : vImageUv;
            float wallMaskAlpha = texture2D(maskMap, wallClipUv).r;
            if (wallMaskAlpha < 0.015) {
              discard;
            }

            gl_FragColor = vec4(patternColor.rgb, patternColor.a * wallMaskAlpha * opacity);
            return;
          }

          if (hasLight > 0.5) {
            float lightValue = texture2D(lightMap, vImageUv).r;
            float lightFactor = clamp(lightValue / 0.50, 0.58, 1.48);
            patternColor.rgb *= lightFactor;
          } else if (hasBase > 0.5) {
            vec4 baseColor = texture2D(baseMap, vImageUv);
            float baseLuma = dot(baseColor.rgb, vec3(0.299, 0.587, 0.114));
            float lightFactor = clamp(baseLuma / 0.58, 0.54, 1.34);
            patternColor.rgb *= lightFactor;
            patternColor.rgb = mix(patternColor.rgb, baseColor.rgb, baseBlend);
          }

          // FLIP CLIP / MASK VERTICAL ONLY.
          // Texture tetap normal, cuma area clipping mask yang dibalik atas-bawah.
          vec2 clipUv = maskFlipY > 0.5
            ? vec2(vImageUv.x, 1.0 - vImageUv.y)
            : vImageUv;

          // Gaussian 9 sample + smoothstep = tepi mask lebih halus.
          float maskAlpha = sampleMaskAlpha9(clipUv);
          maskAlpha = smoothstep(0.18, 0.78, maskAlpha);

          if (maskAlpha < 0.015) {
            discard;
          }

          gl_FragColor = vec4(patternColor.rgb, opacity);
        }
      `
    });
  }

  private previewRepeatScaleForLayer(layer: PreviewLayerInput): number {
    const scale = this.clampNumber(Number(layer.textureScale ?? this.textureScale ?? 1), 0.15, 5);
    const pattern = String(layer.pattern || this.pattern || 'straight');

    // Dinding pakai raw texture kisi, bukan pattern generator.
    // Semakin besar repeatScale, motif panel makin kecil/rapat.
    // Target template room: panel harus rapat seperti preview Python,
    // bukan kebesaran satu bidang.
    if (this.shouldUseRawSurfaceTexture(layer)) {
      return scale * (layer.surface === 'ceiling' ? 2.4 : 2.8);
    }

    if (this.shouldUseRawFloorTexture(layer)) {
      return scale * 1.25;
    }

    // Surface vertikal lain dibuat flat-local per bbox.
    if (!this.shouldUsePerspectiveGeometry(layer)) {
      if (pattern === 'herringbone') {
        return scale * 0.75;
      }

      return scale * 0.85;
    }

    return scale;
  }

  private previewRepeatScaleVecForLayer(layer: PreviewLayerInput): THREE.Vector2 {
    const scale = this.clampNumber(Number(layer.textureScale ?? this.textureScale ?? 1), 0.15, 5);

    if (layer.surface === 'wall') {
      return this.wallRepeatScaleForLayer(layer);
    }

    if (this.shouldUseCeilingProjectiveLayer(layer)) {
      const repeatX = Math.max(1, Number(this.previewCeilingRepeatX || 7));
      const repeatY = Math.max(1, Number(this.previewCeilingRepeatY || 3));
      return new THREE.Vector2(repeatX * scale, repeatY * scale);
    }

    if (this.shouldUseFloorProjectiveLayer(layer)) {
      const pattern = String(layer.pattern || this.pattern || 'straight').toLowerCase();
      if (pattern === 'herringbone') {
        const metrics = this.herringboneTileMetricsForLayer(layer);
        const roomWidth = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30);
        const roomDepth = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40);
        const tileSizeM = Math.max(metrics.tileSizeM, 0.0001);
        return new THREE.Vector2(
          this.clampNumber((roomWidth / tileSizeM) * scale, 0.25, 80),
          this.clampNumber((roomDepth / tileSizeM) * scale, 0.25, 80)
        );
      }

      if (pattern === 'chevron') {
        const metrics = this.chevronTileMetricsForLayer(layer);
        const roomWidth = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30);
        const roomDepth = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40);
        return new THREE.Vector2(
          this.clampNumber((roomWidth / Math.max(metrics.tileWidthM, 0.0001)) * scale, 0.25, 80),
          this.clampNumber((roomDepth / Math.max(metrics.tileHeightM, 0.0001)) * scale, 0.25, 80)
        );
      }

      if (pattern === 'grid') {
        const metrics = this.gridTileMetricsForLayer(layer);
        const roomWidth = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30);
        const roomDepth = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40);
        const surfaceWidthM = Number(layer.surfaceWidthMm || 0) > 0
          ? Number(layer.surfaceWidthMm) / 1000
          : roomWidth;
        const surfaceDepthM =
          Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm || 0) > 0
            ? Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm) / 1000
            : roomDepth;
        return new THREE.Vector2(
          this.clampNumber((surfaceWidthM / Math.max(metrics.tileWidthM, 0.0001)) * scale, 0.25, 140),
          this.clampNumber((surfaceDepthM / Math.max(metrics.tileHeightM, 0.0001)) * scale, 0.25, 140)
        );
      }

      if (pattern === 'brick' || pattern === 'brick_1_3' || pattern === 'mix' || pattern === 'offset' || pattern === 'third') {
        const metrics = this.brickTileMetricsForLayer(layer);
        const roomWidth = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30);
        const roomDepth = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40);
        const surfaceWidthM = Number(layer.surfaceWidthMm || 0) > 0
          ? Number(layer.surfaceWidthMm) / 1000
          : roomWidth;
        const surfaceDepthM =
          Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm || 0) > 0
            ? Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm) / 1000
            : roomDepth;
        return new THREE.Vector2(
          this.clampNumber((surfaceWidthM / Math.max(metrics.tileWidthM, 0.0001)) * scale, 0.25, 140),
          this.clampNumber((surfaceDepthM / Math.max(metrics.tileHeightM, 0.0001)) * scale, 0.25, 140)
        );
      }

      if (pattern === 'chessboard') {
        const metrics = this.chessboardTileMetricsForLayer(layer);
        const roomWidth = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30);
        const roomDepth = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40);
        const surfaceWidthM = Number(layer.surfaceWidthMm || 0) > 0
          ? Number(layer.surfaceWidthMm) / 1000
          : roomWidth;
        const surfaceDepthM =
          Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm || 0) > 0
            ? Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm) / 1000
            : roomDepth;
        return new THREE.Vector2(
          this.clampNumber((surfaceWidthM / Math.max(metrics.tileWidthM, 0.0001)) * scale, 0.25, 140),
          this.clampNumber((surfaceDepthM / Math.max(metrics.tileHeightM, 0.0001)) * scale, 0.25, 140)
        );
      }

      if (pattern === 'basketweave' || pattern === 'basket') {
        const metrics = this.basketweaveTileMetricsForLayer(layer);
        const roomWidth = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30);
        const roomDepth = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40);
        const surfaceWidthM = Number(layer.surfaceWidthMm || 0) > 0
          ? Number(layer.surfaceWidthMm) / 1000
          : roomWidth;
        const surfaceDepthM =
          Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm || 0) > 0
            ? Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm) / 1000
            : roomDepth;
        return new THREE.Vector2(
          this.clampNumber((surfaceWidthM / Math.max(metrics.tileWidthM, 0.0001)) * scale, 0.25, 140),
          this.clampNumber((surfaceDepthM / Math.max(metrics.tileHeightM, 0.0001)) * scale, 0.25, 140)
        );
      }

      const repeatX = Math.max(1, Number(this.previewFloorMainRepeatX || 5))
        + Math.max(0, Number(this.previewFloorExtraRepeatX || 2)) * 2;
      const repeatY = Math.max(1, Number(this.previewFloorRepeatY || 5));
      return new THREE.Vector2(repeatX * scale, repeatY * scale);
    }

    // Floor/countertop pattern canvas sudah dibuat real-scale dari ukuran produk.
    // Jangan dikali surface/product lagi, nanti tile jadi super kecil.
    if (this.shouldUsePerspectiveGeometry(layer)) {
      if (this.shouldUseRawFloorTexture(layer)) {
        const roomWidth = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30);
        const roomDepth = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40);
        const productSize = this.realFloorProductSizeMForLayer(layer);

        if (!this.previewUseRealProductScale) {
          const visualRepeatX = this.clampNumber(Number(this.previewFloorRepeatX || 5), 1, 40);
          const repeatX = this.clampNumber(visualRepeatX * scale, 0.25, 80);
          const repeatY = this.clampNumber(
            repeatX * (roomDepth / roomWidth) * (productSize.widthM / productSize.lengthM),
            0.25,
            80
          );

          return new THREE.Vector2(repeatX, repeatY);
        }

        const repeatX = this.clampNumber((roomWidth / productSize.widthM) * scale, 0.25, 140);
        const repeatY = this.clampNumber((roomDepth / productSize.lengthM) * scale, 0.25, 140);
        return new THREE.Vector2(repeatX, repeatY);
      }

      return new THREE.Vector2(scale, scale);
    }

    const surfaceWidthMm = Number(layer.surfaceWidthMm || 0);
    const surfaceHeightMm = Number(layer.surfaceHeightMm || 0);
    const surfaceLengthMm = Number(layer.surfaceLengthMm || layer.surfaceDepthMm || 0);
    const productWidthMm = Number(layer.productWidthMm || 0);
    const productHeightMm = Number(layer.productHeightMm || 0);

    if (surfaceWidthMm > 0 && productWidthMm > 0) {
      const repeatX = this.clampNumber((surfaceWidthMm / productWidthMm) * scale, 0.25, 80);
      const verticalSizeMm = surfaceLengthMm;
      const repeatY = verticalSizeMm > 0 && productHeightMm > 0
        ? this.clampNumber((verticalSizeMm / productHeightMm) * scale, 0.25, 80)
        : repeatX;

      return new THREE.Vector2(repeatX, repeatY);
    }

    const repeat = this.previewRepeatScaleForLayer(layer);
    return new THREE.Vector2(repeat, repeat);
  }

  private wallRepeatScaleForLayer(layer: PreviewLayerInput): THREE.Vector2 {
    const repeatX = Math.max(1, Number(this.previewWallRepeatX || 8));
    const repeatY = Math.max(1, Number(this.previewWallRepeatY || 1));
    const zoom = Math.max(0.05, Number(layer.textureScale ?? this.textureScale ?? 1));

    return new THREE.Vector2(repeatX / zoom, repeatY / zoom);
  }

  private previewBaseBlendForLayer(layer: PreviewLayerInput): number {
    if (layer.surface === 'wall') {
      return 0.04;
    }

    if (layer.surface === 'ceiling') {
      return 0.24;
    }

    return 0.10;
  }

  private previewTextureFlipYForLayer(layer: PreviewLayerInput): boolean {
    return false;
  }

  private gridPhysicalRotationEnabledForLayer(layer: PreviewLayerInput): boolean {
    const pattern = String(layer.pattern || this.pattern || 'straight').toLowerCase();
    return (pattern === 'grid' || pattern === 'brick' || pattern === 'brick_1_3' || pattern === 'third' || pattern === 'herringbone') && this.shouldUseFloorProjectiveLayer(layer);
  }

  private floorSurfaceSizeVecForLayer(layer: PreviewLayerInput): THREE.Vector2 {
    const roomWidth = this.clampNumber(Number(layer.roomWidthM || this.roomWidthM || 4), 0.5, 30);
    const roomDepth = this.clampNumber(Number(layer.roomDepthM || this.roomDepthM || 5), 0.5, 40);
    const surfaceWidthM = Number(layer.surfaceWidthMm || 0) > 0
      ? Number(layer.surfaceWidthMm) / 1000
      : roomWidth;
    const surfaceDepthM =
      Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm || 0) > 0
        ? Number(layer.surfaceLengthMm || layer.surfaceDepthMm || layer.surfaceHeightMm) / 1000
        : roomDepth;

    return new THREE.Vector2(Math.max(surfaceWidthM, 0.0001), Math.max(surfaceDepthM, 0.0001));
  }

  private patternRotationRadForLayer(layer: PreviewLayerInput): number {
    if (this.shouldUseFloorProjectiveLayer(layer)) {
      return THREE.MathUtils.degToRad(
        Number(layer.rotationAngle ?? this.previewFloorPatternRotationDeg ?? 0)
      );
    }

    if (layer.surface === 'wall') {
      return THREE.MathUtils.degToRad(
        Number(layer.rotationAngle ?? this.previewWallRotationDeg ?? 0)
      );
    }

    return THREE.MathUtils.degToRad(
      Number(layer.rotationAngle ?? this.textureRotation ?? 0)
    );
  }

  private shouldUseRawFloorPlankShaderForLayer(layer: PreviewLayerInput): boolean {
    if (!this.shouldUseFloorProjectiveLayer(layer)) {
      return false;
    }

    return !!this.previewRawFloorPlankShader;
  }

  private realFloorProductSizeMForLayer(layer: PreviewLayerInput): { widthM: number; lengthM: number } {
    const widthFromProductMm = Number(layer.productWidthMm ?? 0) > 0 ? Number(layer.productWidthMm) / 1000 : 0;
    const lengthFromProductMm = Number(layer.productHeightMm ?? 0) > 0 ? Number(layer.productHeightMm) / 1000 : 0;

    let widthM = widthFromProductMm || Number(layer.plankWidthM ?? this.plankWidthM ?? 0.1524);
    let lengthM = lengthFromProductMm || Number(layer.plankLengthM ?? this.plankLengthM ?? 0.9144);

    widthM = this.clampNumber(widthM, 0.03, 3);
    lengthM = this.clampNumber(lengthM, 0.03, 6);

    if (lengthM < widthM) {
      const temp = widthM;
      widthM = lengthM;
      lengthM = temp;
    }

    return { widthM, lengthM };
  }

  private shouldUseRawFloorTexture(layer: PreviewLayerInput): boolean {
    if (layer.surface !== 'floor' && layer.surface !== 'countertop') {
      return false;
    }

    const pattern = String(layer.pattern || this.pattern || 'straight').toLowerCase();
    return pattern === 'tile' || pattern === 'grid' || pattern === 'straight';
  }

  private shouldUseDirectRawFloorTexture(layer: PreviewLayerInput): boolean {
    return !!this.previewUseRawFloorTexture && this.shouldUseRawFloorTexture(layer);
  }

  private configureDirectRawFloorTexture(texture: THREE.Texture): void {
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
    texture.repeat.set(1, 1);
    texture.offset.set(0, 0);
    texture.rotation = 0;
    texture.center.set(0, 0);
    texture.needsUpdate = true;
  }

  private createQuadGeometryForPoints(points: PerspectivePoints): THREE.BufferGeometry {
    const tl = this.imagePointToWorld(points.topLeft);
    const tr = this.imagePointToWorld(points.topRight);
    const br = this.imagePointToWorld(points.bottomRight);
    const bl = this.imagePointToWorld(points.bottomLeft);

    const vertices = new Float32Array([
      tl.x, tl.y, 0,
      tr.x, tr.y, 0,
      br.x, br.y, 0,
      bl.x, bl.y, 0
    ]);

    const uvs = new Float32Array([
      0, 1,
      1, 1,
      1, 0,
      0, 0
    ]);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.computeVertexNormals();

    return geometry;
  }


  private createPreviewMesh(texture: THREE.Texture): THREE.Mesh {
    const singleLayer: PreviewLayerInput = {
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: this.previewTextureUrl || '',
      maskUrl: this.maskImageUrl || '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      pattern: this.pattern,
      textureScale: this.textureScale,
      rotationAngle: this.textureRotation,
      plankWidthM: this.plankWidthM,
      plankLengthM: this.plankLengthM,
      groutWidthM: this.groutWidthM,
      groutEnabled: this.groutEnabled,
      groutSizeMm: this.groutSizeMm,
      groutColor: this.groutColor
    };
    const geometry =
      this.activeSurface === 'floor' ||
      this.activeSurface === 'countertop' ||
      this.activeSurface === 'ceiling' ||
      this.activeSurface === 'wall'
      ? this.createFullImageGeometry()
      : this.createQuadGeometry();

    this.previewMaterial = new THREE.ShaderMaterial({
      transparent: true,
      extensions: { derivatives: true } as any,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: {
        patternMap: { value: texture },
        maskMap: { value: this.maskTexture },
        hasMask: { value: this.maskTexture ? 1 : 0 },
        opacity: { value: this.previewOpacity },
        imageWidth: { value: this.imageWidth },
        imageHeight: { value: this.imageHeight },
        repeatScale: { value: this.previewRepeatScale() },
        repeatScaleVec: { value: this.singlePreviewRepeatScaleVec() },
        gridPhysicalRotation: { value: this.gridPhysicalRotationEnabledForLayer(singleLayer) ? 1 : 0 },
        floorSurfaceSizeM: { value: this.floorSurfaceSizeVecForLayer(singleLayer) },
        useProjectiveUv: { value: this.shouldUseProjectiveUvForLayer(singleLayer) ? 1 : 0 },
        projectiveUvMatrix: { value: this.projectiveUvMatrixForLayer(singleLayer) },
        rotationRad: { value: this.patternRotationRadForLayer(singleLayer) },
        offset: { value: new THREE.Vector2(Number(this.textureOffsetX || 0), Number(this.textureOffsetY || 0)) },
        groutEnabled: { value: this.groutEnabledForLayer(singleLayer) ? 1 : 0 },
        groutLineSize: { value: this.groutLineSizeForLayer(singleLayer) },
        groutColor: { value: this.groutColorForLayer(singleLayer) },
        compareX: { value: this.clampNumber(this.comparePosition, 0, 1) },
        isCompare: { value: this.mode === 'compare' ? 1 : 0 }
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec2 vImageUv;

        uniform float imageWidth;
        uniform float imageHeight;

        void main() {
          vUv = uv;

          vImageUv = vec2(
            (position.x + imageWidth * 0.5) / imageWidth,
            (imageHeight * 0.5 - position.y) / imageHeight
          );

          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D patternMap;
        uniform sampler2D maskMap;
        uniform float hasMask;
        uniform float opacity;
        uniform float repeatScale;
        uniform vec2 repeatScaleVec;
        uniform float gridPhysicalRotation;
        uniform vec2 floorSurfaceSizeM;
        uniform float useProjectiveUv;
        uniform mat3 projectiveUvMatrix;
        uniform float rotationRad;
        uniform vec2 offset;
        uniform float groutEnabled;
        uniform vec2 groutLineSize;
        uniform vec3 groutColor;
        uniform float compareX;
        uniform float isCompare;

        varying vec2 vUv;
        varying vec2 vImageUv;

        vec2 projectiveFloorUv(vec2 imageUv, vec2 fallbackUv) {
          if (useProjectiveUv < 0.5) {
            return fallbackUv;
          }

          vec3 projected = projectiveUvMatrix * vec3(imageUv, 1.0);
          float w = abs(projected.z) < 0.00001 ? 0.00001 : projected.z;
          return projected.xy / w;
        }

        vec2 rotateUv(vec2 uv, float angle) {
          vec2 centered = uv - vec2(0.5);
          float c = cos(angle);
          float s = sin(angle);
          mat2 rot = mat2(c, -s, s, c);
          return rot * centered + vec2(0.5);
        }

        vec2 rotateUvAspect(vec2 uv, float angleRad, vec2 aspect) {
          vec2 center = vec2(0.5);
          vec2 p = uv - center;
          p.x *= aspect.x;
          p.y *= aspect.y;

          float c = cos(angleRad);
          float s = sin(angleRad);
          vec2 r = vec2(
            p.x * c - p.y * s,
            p.x * s + p.y * c
          );

          r.x /= aspect.x;
          r.y /= aspect.y;
          return r + center;
        }

        float groutLineAlpha(vec2 tiledUv) {
          if (groutEnabled < 0.5 || max(groutLineSize.x, groutLineSize.y) <= 0.0) {
            return 0.0;
          }

          vec2 gap = clamp(groutLineSize, vec2(0.0), vec2(0.45));
          vec2 period = vec2(1.0) + gap;
          vec2 local = mod(tiledUv, period);
          vec2 aa = max(fwidth(tiledUv) * 1.10, vec2(0.00035));
          float vertical = smoothstep(1.0 - aa.x, 1.0 + aa.x, local.x) *
            (1.0 - smoothstep(period.x - aa.x, period.x, local.x));
          float horizontal = smoothstep(1.0 - aa.y, 1.0 + aa.y, local.y) *
            (1.0 - smoothstep(period.y - aa.y, period.y, local.y));
          return clamp(max(vertical, horizontal), 0.0, 1.0);
        }

        vec2 groutProductSampleUv(vec2 tiledUv) {
          if (groutEnabled < 0.5 || max(groutLineSize.x, groutLineSize.y) <= 0.0) {
            return tiledUv;
          }

          vec2 gap = clamp(groutLineSize, vec2(0.0), vec2(0.45));
          vec2 period = vec2(1.0) + gap;
          vec2 cell = floor(tiledUv / period);
          vec2 local = mod(tiledUv, period);
          return cell + clamp(local, vec2(0.0), vec2(0.9999));
        }

        void main() {
          if (isCompare > 0.5 && vImageUv.x > compareX) {
            discard;
          }

          vec2 floorUv = projectiveFloorUv(vImageUv, vUv);
          vec2 patternUv;
          if (gridPhysicalRotation > 0.5) {
            vec2 surfaceOffset = offset / max(repeatScaleVec, vec2(0.0001));
            floorUv = rotateUvAspect(floorUv + surfaceOffset, rotationRad, floorSurfaceSizeM);
            patternUv = floorUv * repeatScaleVec;
          } else {
            patternUv = floorUv * repeatScaleVec + offset;
            vec2 center = repeatScaleVec * 0.5;
            vec2 p = patternUv - center;
            float c = cos(rotationRad);
            float s = sin(rotationRad);
            patternUv = vec2(
              p.x * c - p.y * s,
              p.x * s + p.y * c
            ) + center;
          }

          vec2 textureUv = groutProductSampleUv(patternUv);
          vec4 patternColor = texture2D(patternMap, textureUv);
          float groutAlpha = groutLineAlpha(patternUv);
          patternColor.rgb = mix(patternColor.rgb, groutColor, groutAlpha);

          float maskAlpha = 1.0;

          if (hasMask > 0.5) {
            vec4 maskColor = texture2D(maskMap, vImageUv);
            float maskLuma = max(maskColor.r, max(maskColor.g, maskColor.b));
            maskAlpha = maskLuma * maskColor.a;
          }

          if (maskAlpha < 0.45) {
            discard;
          }

          gl_FragColor = vec4(patternColor.rgb, patternColor.a * maskAlpha * opacity);
        }
      `
    });

    const mesh = new THREE.Mesh(geometry, this.previewMaterial);
    mesh.position.z = 2;
    mesh.renderOrder = 20;
    return mesh;
  }

  private createQuadGeometry(): THREE.BufferGeometry {
    const points = this.getEditablePointsOrDefault();
    const tl = this.imagePointToWorld(points.topLeft);
    const tr = this.imagePointToWorld(points.topRight);
    const br = this.imagePointToWorld(points.bottomRight);
    const bl = this.imagePointToWorld(points.bottomLeft);

    const vertices = new Float32Array([
      tl.x, tl.y, 0,
      tr.x, tr.y, 0,
      br.x, br.y, 0,
      bl.x, bl.y, 0
    ]);
    const uvs = new Float32Array([
      0, 1,
      1, 1,
      1, 0,
      0, 0
    ]);
    const indices = [0, 1, 2, 0, 2, 3];
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }

  private updatePreviewGeometry(): void {
    if (!this.previewMesh || this.imageWidth <= 0 || this.imageHeight <= 0) {
      return;
    }
    const oldGeometry = this.previewMesh.geometry;
    this.previewMesh.geometry =
      this.activeSurface === 'floor' ||
      this.activeSurface === 'countertop' ||
      this.activeSurface === 'ceiling' ||
      this.activeSurface === 'wall'
      ? this.createFullImageGeometry()
      : this.createQuadGeometry();
    oldGeometry.dispose();
    this.scheduleRender();
  }

  private async rebuildPreviewTextureOnly(): Promise<void> {
    if (!this.sourceProductTexture) {
      return;
    }

    const singleLayer: PreviewLayerInput = {
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: this.previewTextureUrl || '',
      maskUrl: this.maskImageUrl || '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      pattern: this.pattern,
      roomWidthM: this.roomWidthM,
      roomDepthM: this.roomDepthM,
      textureScale: this.textureScale,
      rotationAngle: this.textureRotation,
      plankWidthM: this.plankWidthM,
      plankLengthM: this.plankLengthM,
      groutWidthM: this.groutWidthM,
      groutEnabled: this.groutEnabled,
      groutSizeMm: this.groutSizeMm,
      groutColor: this.groutColor
    };

    const image = this.sourceProductTexture.image as CanvasImageSource;
    let texture: THREE.Texture;

    if (singleLayer.surface === 'wall' && this.previewWallUsePanelTexture) {
      texture = this.createWallPanelTexture(image);
      this.disposableGeneratedTextures.add(texture);
    } else if (this.shouldUseCeilingProjectiveLayer(singleLayer) && this.previewCeilingUseTileCellTexture) {
      texture = this.createCeilingTileCellTexture(image);
      this.disposableGeneratedTextures.add(texture);
    } else if (this.shouldUseFloorTileCellTexture(singleLayer)) {
      texture = this.createFloorTileCellTexture(image, singleLayer);
      this.disposableGeneratedTextures.add(texture);
    } else if (this.shouldUseDirectRawFloorTexture(singleLayer)) {
      texture = this.sourceProductTexture;
      this.configureDirectRawFloorTexture(texture);
    } else if (this.shouldUseRawSurfaceTexture(singleLayer)) {
      texture = this.createRawSurfaceTexture(image);
      this.disposableGeneratedTextures.add(texture);
    } else {
      texture = this.createPatternTexture(image, this.activeSurface);
      this.disposableGeneratedTextures.add(texture);
    }

    this.disposeTextureIfDisposable(this.previewTexture);
    this.previewTexture = texture;

    if (this.previewMaterial) {
      this.previewMaterial.uniforms['patternMap'].value = texture;
      this.previewMaterial.uniforms['maskMap'].value = this.maskTexture;
      this.previewMaterial.uniforms['hasMask'].value = this.maskTexture ? 1 : 0;
      this.previewMaterial.uniforms['opacity'].value = this.previewOpacity;
      this.previewMaterial.uniforms['imageWidth'].value = this.imageWidth;
      this.previewMaterial.uniforms['imageHeight'].value = this.imageHeight;
      this.previewMaterial.needsUpdate = true;
    }

    this.applyPatternTransform(texture);
    this.applyRuntimeUniforms();
    this.scheduleRender();
  }

  private shouldUseRawSurfaceTexture(layer: PreviewLayerInput): boolean {
    // Untuk dinding, motif produk harus tampil apa adanya.
    // Jadi pola lantai seperti bata, 1/3, random, herringbone, dll tidak diterapkan.
    return layer.surface === 'wall' || layer.surface === 'ceiling';
  }

  private shouldUseFloorTileCellTexture(layer: PreviewLayerInput): boolean {
    return !!this.previewFloorUseTileCellTexture && this.shouldUseFloorProjectiveLayer(layer);
  }

  private createRawSurfaceTexture(image: CanvasImageSource): THREE.CanvasTexture {
    const sourceSize = this.imageSourceSize(image);
    const canvasSize = this.previewPreserveProductAspect
      ? this.rawTextureCanvasSize(sourceSize.width, sourceSize.height)
      : { width: 2048, height: 2048 };

    const canvas = document.createElement('canvas');
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return new THREE.CanvasTexture(canvas);
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    if (this.previewPreserveProductAspect) {
      this.drawImageNoCrop(ctx, image, 0, 0, canvas.width, canvas.height);
    } else {
      this.drawImageCover(ctx, image, 0, 0, canvas.width, canvas.height);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
    texture.needsUpdate = true;

    this.applyPatternTransform(texture);
    return texture;
  }

  private createFloorTileCellTexture(image: CanvasImageSource, layer?: PreviewLayerInput): THREE.CanvasTexture {
    const pattern = String(layer?.pattern || this.pattern || 'straight').toLowerCase();

    if (pattern === 'herringbone') {
      return this.createHerringboneMosaicTexture(image, 'floor');
    }

    if (pattern === 'chevron') {
      return this.createChevronMosaicTexture(image, 'floor');
    }

    if (pattern === 'brick' || pattern === 'brick_1_3' || pattern === 'mix' || pattern === 'offset' || pattern === 'third') {
      return this.createBrickMosaicTexture(image, 'floor', layer);
    }

    if (pattern === 'grid') {
      return this.createGridMosaicTexture(image, 'floor', layer);
    }

    if (pattern === 'chessboard') {
      return this.createChessboardMosaicTexture(image, 'floor', layer);
    }

    if (pattern === 'basketweave' || pattern === 'basket') {
      return this.createBasketweaveMosaicTexture(image, 'floor', layer);
    }

    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return new THREE.CanvasTexture(canvas);
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    this.drawImageCover(ctx, image, 0, 0, size, size);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
    texture.needsUpdate = true;
    return texture;
  }

  private createCeilingTileCellTexture(image: CanvasImageSource): THREE.CanvasTexture {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return new THREE.CanvasTexture(canvas);
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    this.drawImageCover(ctx, image, 0, 0, size, size);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
    texture.needsUpdate = true;
    return texture;
  }

  private createWallPanelTexture(image: CanvasImageSource): THREE.CanvasTexture {
    const width = 512;
    const height = 2048;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return new THREE.CanvasTexture(canvas);
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    this.drawImageCover(ctx, image, 0, 0, width, height);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;

    const maxAniso = this.renderer?.capabilities?.getMaxAnisotropy?.() ?? 8;
    texture.anisotropy = Math.min(16, maxAniso);

    texture.needsUpdate = true;
    return texture;
  }

  private imageSourceSize(image: CanvasImageSource): { width: number; height: number } {
    const source = image as { width?: number; height?: number; videoWidth?: number; videoHeight?: number; naturalWidth?: number; naturalHeight?: number };
    const width = Number(source.naturalWidth || source.width || source.videoWidth || 1);
    const height = Number(source.naturalHeight || source.height || source.videoHeight || 1);

    return {
      width: Math.max(1, Math.round(width)),
      height: Math.max(1, Math.round(height))
    };
  }

  private rawTextureCanvasSize(sourceWidth: number, sourceHeight: number): { width: number; height: number } {
    const maxSide = this.clampNumber(Number(this.previewRawTextureMaxSide || 4096), 1024, 8192);
    const ratio = sourceWidth / Math.max(sourceHeight, 1);

    if (sourceWidth >= sourceHeight) {
      return {
        width: Math.round(maxSide),
        height: Math.max(1, Math.round(maxSide / Math.max(ratio, 0.0001)))
      };
    }

    return {
      width: Math.max(1, Math.round(maxSide * ratio)),
      height: Math.round(maxSide)
    };
  }

  private drawImageNoCrop(
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    const sourceSize = this.imageSourceSize(image);
    ctx.drawImage(
      image,
      0,
      0,
      sourceSize.width,
      sourceSize.height,
      x,
      y,
      width,
      height
    );
  }

  private createPatternTexture(image: CanvasImageSource, surface: TargetSurface = this.activeSurface): THREE.CanvasTexture {
    const { width, height, pixelsPerMeter } = this.patternCanvasSize(surface);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return new THREE.CanvasTexture(canvas);
    }

    ctx.fillStyle = '#d9d3c3';
    ctx.fillRect(0, 0, width, height);

   switch (this.pattern) {
      case 'herringbone':
        this.drawHerringbonePattern(ctx, image, width, height, pixelsPerMeter, surface);
        break;

      case 'chevron':
        this.drawChevronPattern(ctx, image, width, height, pixelsPerMeter, surface);
        break;

      case 'basket':
      case 'basketweave':
        this.drawBasketweavePattern(ctx, image, width, height, pixelsPerMeter, surface);
        break;

      case 'tile':
      case 'grid':
        this.drawGridPattern(ctx, image, width, height, pixelsPerMeter, surface);
        break;

      case 'chessboard':
        this.drawChessboardPattern(ctx, image, width, height, pixelsPerMeter, surface);
        break;

      case 'brick':
      case 'brick_1_3':
      case 'mix':
      case 'third':
      case 'offset':
        this.drawBrickPattern(ctx, image, width, height, pixelsPerMeter, surface);
        break;

      case 'random':
      case 'straight':
      default:
        this.drawStraightPattern(ctx, image, width, height, pixelsPerMeter);
        break;
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
    this.applyPatternTransform(texture);
    return texture;
  }

  private patternCanvasSize(surface: TargetSurface): { width: number; height: number; pixelsPerMeter: number } {
    const isRealScaleSurface = surface === 'floor' || surface === 'countertop';
    if (!isRealScaleSurface) {
      return { width: 2048, height: 2048, pixelsPerMeter: 900 };
    }

    const roomWidth = this.clampNumber(Number(this.roomWidthM || 4), 1.5, 20);
    const roomDepth = this.clampNumber(Number(this.roomDepthM || 5), 1.5, 30);
    const requestedPpm = this.clampNumber(Number(this.pixelsPerMeter || 900), 300, 1600);
    const maxTextureSide = 4096;
    const ppm = Math.max(120, Math.min(requestedPpm, maxTextureSide / Math.max(roomWidth, roomDepth)));

    return {
      width: Math.max(512, Math.round(roomWidth * ppm)),
      height: Math.max(512, Math.round(roomDepth * ppm)),
      pixelsPerMeter: ppm,
    };
  }

private drawGridPattern(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  surface: TargetSurface
): void {
  const tile = this.createGridTileCanvas(image, this.gridTileMetrics(surface, undefined, pixelsPerMeter));
  const pattern = ctx.createPattern(tile, 'repeat');
  if (!pattern) {
    this.renderGridMosaic(ctx, image, width, height, pixelsPerMeter);
    return;
  }

  ctx.save();
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

private createGridMosaicTexture(image: CanvasImageSource, surface: TargetSurface, layer?: PreviewLayerInput): THREE.CanvasTexture {
  const canvas = this.createGridTileCanvas(image, this.gridTileMetrics(surface, layer));

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
  texture.needsUpdate = true;
  return texture;
}

private createGridTileCanvas(image: CanvasImageSource, metrics: GridTileMetrics): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = metrics.tileWidthPx;
  canvas.height = metrics.tileHeightPx;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  this.renderGridMosaic(ctx, image, canvas.width, canvas.height, metrics.pixelsPerMeter, metrics);
  return canvas;
}

private renderGridMosaic(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  metrics?: GridTileMetrics
): void {
  const tileMetrics = metrics || this.gridTileMetrics(this.activeSurface, undefined, pixelsPerMeter);
  const seamWidth = tileMetrics.seamWidthPx;
  const inset = seamWidth / 2;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = tileMetrics.seamColor;
  ctx.fillRect(0, 0, width, height);
  const plankSources = this.createOrientedPlankSources(image, tileMetrics.plankW, tileMetrics.plankL);
  ctx.drawImage(plankSources.horizontal, inset, inset, tileMetrics.plankL, tileMetrics.plankW);
  ctx.restore();
}

private gridTileMetrics(
  surface: TargetSurface,
  layer?: PreviewLayerInput,
  pixelsPerMeterOverride?: number
): GridTileMetrics {
  const isRealScaleSurface = surface === 'floor' || surface === 'countertop';
  const roomWidth = this.clampNumber(Number(layer?.roomWidthM || this.roomWidthM || 4), 0.5, 30);
  const roomDepth = this.clampNumber(Number(layer?.roomDepthM || this.roomDepthM || 5), 0.5, 40);
  const requestedPpm = this.clampNumber(Number(pixelsPerMeterOverride || layer?.pixelsPerMeter || this.pixelsPerMeter || 900), 300, 1600);
  const productWidthM = Number(layer?.productWidthMm || 0) > 0
    ? Number(layer?.productWidthMm) / 1000
    : 0;
  const productHeightM = Number(layer?.productHeightMm || 0) > 0
    ? Number(layer?.productHeightMm) / 1000
    : 0;
  const widthFromSettingsM = Number(layer?.plankWidthM || this.plankWidthM || 0.184);
  const lengthFromSettingsM = Number(layer?.plankLengthM || this.plankLengthM || 1.22);
  const rawWidthM = Math.max(0.01, productWidthM || widthFromSettingsM);
  const rawLengthM = Math.max(0.01, productHeightM || lengthFromSettingsM);
  const realWidthM = Math.min(rawWidthM, rawLengthM);
  const realLengthM = Math.max(rawWidthM, rawLengthM);
  const legacySizeMm = Number(layer?.groutWidthM ?? this.groutWidthM ?? 0) * 1000;
  const groutEnabled = !!(layer?.groutEnabled ?? this.groutEnabled);
  const groutSizeMm = this.clampNumber(Number(layer?.groutSizeMm ?? this.groutSizeMm ?? legacySizeMm ?? 0), 0, 80);
  const seamWidthM = groutEnabled ? groutSizeMm / 1000 : 0;
  const tileWidthM = realLengthM + seamWidthM;
  const tileHeightM = realWidthM + seamWidthM;
  const roomLimitedPpm = isRealScaleSurface
    ? Math.min(requestedPpm, 4096 / Math.max(roomWidth, roomDepth))
    : requestedPpm;
  const tileLimitedPpm = Math.min(roomLimitedPpm, 4096 / Math.max(tileWidthM, tileHeightM, 0.0001));
  const ppm = Math.max(120, tileLimitedPpm);
  const plankW = Math.max(12, Math.round(realWidthM * ppm));
  const plankL = Math.max(plankW, Math.round(realLengthM * ppm));
  const seamWidthPx = groutEnabled ? this.clampNumber((groutSizeMm / 1000) * ppm, 1, 12) : 0;
  const tileWidthPx = this.clampNumber(Math.round(plankL + seamWidthPx), 32, 4096);
  const tileHeightPx = this.clampNumber(Math.round(plankW + seamWidthPx), 32, 4096);

  return {
    tileWidthPx,
    tileHeightPx,
    tileWidthM: tileWidthPx / ppm,
    tileHeightM: tileHeightPx / ppm,
    pixelsPerMeter: ppm,
    plankW,
    plankL,
    seamWidthPx,
    seamColor: groutEnabled ? String(layer?.groutColor || this.groutColor || '#CD9A6C') : '#000000'
  };
}

private gridTileMetricsForLayer(layer: PreviewLayerInput): GridTileMetrics {
  return this.gridTileMetrics(layer.surface, layer);
}

private drawBrickPattern(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  surface: TargetSurface
): void {
  const tile = this.createBrickTileCanvas(image, this.brickTileMetrics(surface, undefined, pixelsPerMeter));
  const pattern = ctx.createPattern(tile, 'repeat');
  if (!pattern) {
    this.renderBrickMosaic(ctx, image, width, height, pixelsPerMeter);
    return;
  }

  ctx.save();
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

private createBrickMosaicTexture(image: CanvasImageSource, surface: TargetSurface, layer?: PreviewLayerInput): THREE.CanvasTexture {
  const canvas = this.createBrickTileCanvas(image, this.brickTileMetrics(surface, layer));

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
  texture.needsUpdate = true;
  return texture;
}

private createBrickTileCanvas(image: CanvasImageSource, metrics: BrickTileMetrics): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = metrics.tileWidthPx;
  canvas.height = metrics.tileHeightPx;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  this.renderBrickMosaic(ctx, image, canvas.width, canvas.height, metrics.pixelsPerMeter, metrics);
  return canvas;
}

private renderBrickMosaic(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  metrics?: BrickTileMetrics
): void {
  const tileMetrics = metrics || this.brickTileMetrics(this.activeSurface, undefined, pixelsPerMeter);
  const seamWidth = this.brickSeamWidthPx(tileMetrics.pixelsPerMeter);
  const seamColor = this.brickSeamColor(image);
  const cellW = tileMetrics.plankL + seamWidth;
  const rowH = tileMetrics.plankW + seamWidth;
  const rowOffsetRatios = tileMetrics.rowOffsetRatios.length > 0
    ? tileMetrics.rowOffsetRatios
    : [0];

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = seamColor;
  ctx.fillRect(0, 0, width, height);
  const planks = this.createOrientedPlankSources(image, tileMetrics.plankW, tileMetrics.plankL);

  const rowCount = Math.ceil(height / Math.max(rowH, 1)) + 2;
  for (let row = -1; row <= rowCount; row += 1) {
    const offsetIndex = ((row % rowOffsetRatios.length) + rowOffsetRatios.length) % rowOffsetRatios.length;
    const offset = cellW * rowOffsetRatios[offsetIndex];
    const y = row * rowH + seamWidth / 2;

    for (let x = -cellW * (tileMetrics.tileColumnCount + 1); x < width + cellW; x += cellW) {
      this.drawBrickPlank(ctx, planks.horizontal, x + offset + seamWidth / 2, y, tileMetrics.plankL, tileMetrics.plankW);
    }
  }

  ctx.restore();
}

private brickTileMetrics(
  surface: TargetSurface,
  layer?: PreviewLayerInput,
  pixelsPerMeterOverride?: number
): BrickTileMetrics {
  const isRealScaleSurface = surface === 'floor' || surface === 'countertop';
  const roomWidth = this.clampNumber(Number(layer?.roomWidthM || this.roomWidthM || 4), 0.5, 30);
  const roomDepth = this.clampNumber(Number(layer?.roomDepthM || this.roomDepthM || 5), 0.5, 40);
  const requestedPpm = this.clampNumber(Number(pixelsPerMeterOverride || layer?.pixelsPerMeter || this.pixelsPerMeter || 900), 300, 1600);
  const roomLimitedPpm = isRealScaleSurface
    ? Math.min(requestedPpm, 4096 / Math.max(roomWidth, roomDepth))
    : requestedPpm;
  const productWidthM = Number(layer?.productWidthMm || 0) > 0
    ? Number(layer?.productWidthMm) / 1000
    : 0;
  const productHeightM = Number(layer?.productHeightMm || 0) > 0
    ? Number(layer?.productHeightMm) / 1000
    : 0;
  const widthFromSettingsM = Number(layer?.plankWidthM || this.plankWidthM || 0.184);
  const lengthFromSettingsM = Number(layer?.plankLengthM || this.plankLengthM || 1.22);
  const rawWidthM = Math.max(0.01, productWidthM || widthFromSettingsM);
  const rawLengthM = Math.max(0.01, productHeightM || lengthFromSettingsM);
  const realWidthM = Math.min(rawWidthM, rawLengthM);
  const realLengthM = Math.max(rawWidthM, rawLengthM);
  const pattern = String(layer?.pattern || this.pattern || 'brick').toLowerCase();
  const rowOffsetRatios = this.brickRowOffsetRatios(pattern);
  const tileColumnCount = pattern === 'mix'
    ? 6
    : pattern === 'brick_1_3' || pattern === 'third'
      ? 3
      : 2;
  const legacySizeMm = Number(layer?.groutWidthM ?? this.groutWidthM ?? 0) * 1000;
  const groutEnabled = !!(layer?.groutEnabled ?? this.groutEnabled);
  const groutSizeMm = this.clampNumber(Number(layer?.groutSizeMm ?? this.groutSizeMm ?? legacySizeMm ?? 0), 0, 80);
  const seamWidthM = groutEnabled
    ? groutSizeMm / 1000
    : 0;
  const tileWidthM = realLengthM * tileColumnCount + seamWidthM * tileColumnCount;
  const tileHeightM = realWidthM * rowOffsetRatios.length + seamWidthM * rowOffsetRatios.length;
  const tileLimitedPpm = Math.min(roomLimitedPpm, 4096 / Math.max(tileWidthM, tileHeightM, 0.0001));
  const ppm = Math.max(120, tileLimitedPpm);
  const plankW = Math.max(12, Math.round(realWidthM * ppm));
  const plankL = Math.max(plankW, Math.round(realLengthM * ppm));
  const seamWidthPx = groutEnabled ? this.clampNumber((groutSizeMm / 1000) * ppm, 1, 12) : 0;
  const tileWidthPx = this.clampNumber(Math.round((plankL + seamWidthPx) * tileColumnCount), 128, 4096);
  const tileHeightPx = this.clampNumber(Math.round((plankW + seamWidthPx) * rowOffsetRatios.length), 64, 4096);

  return {
    tileWidthPx,
    tileHeightPx,
    tileWidthM: tileWidthPx / ppm,
    tileHeightM: tileHeightPx / ppm,
    pixelsPerMeter: ppm,
    plankW,
    plankL,
    rowOffsetRatios,
    tileColumnCount
  };
}

private brickTileMetricsForLayer(layer: PreviewLayerInput): BrickTileMetrics {
  return this.brickTileMetrics(layer.surface, layer);
}

private brickRowOffsetRatios(pattern: string): number[] {
  if (pattern === 'brick_1_3' || pattern === 'third') {
    return [0, 1 / 3, 2 / 3];
  }

  if (pattern === 'mix') {
    return [0, 1 / 2, 1 / 3, 0, 2 / 3, 1 / 2];
  }

  return [0, 1 / 2];
}

private drawBrickPlank(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();
  ctx.drawImage(source, x, y, width, height);
  ctx.restore();
}

private brickSeamWidthPx(pixelsPerMeter: number): number {
  if (!this.groutEnabled) {
    return 0;
  }

  return Math.max(1, this.herringboneSeamWidthPx(pixelsPerMeter));
}

private brickSeamColor(image: CanvasImageSource): string {
  if (this.groutEnabled) {
    return String(this.groutColor || '#CD9A6C');
  }

  return this.averageImageColor(image);
}

private drawChessboardPattern(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  surface: TargetSurface
): void {
  const tile = this.createChessboardTileCanvas(image, this.chessboardTileMetrics(surface, undefined, pixelsPerMeter));
  const pattern = ctx.createPattern(tile, 'repeat');
  if (!pattern) {
    this.renderChessboardMosaic(ctx, image, width, height, pixelsPerMeter);
    return;
  }

  ctx.save();
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

private createChessboardMosaicTexture(image: CanvasImageSource, surface: TargetSurface, layer?: PreviewLayerInput): THREE.CanvasTexture {
  const canvas = this.createChessboardTileCanvas(image, this.chessboardTileMetrics(surface, layer));

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
  texture.needsUpdate = true;
  return texture;
}

private createChessboardTileCanvas(image: CanvasImageSource, metrics: ChessboardTileMetrics): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = metrics.tileWidthPx;
  canvas.height = metrics.tileHeightPx;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  this.renderChessboardMosaic(ctx, image, canvas.width, canvas.height, metrics.pixelsPerMeter, metrics);
  return canvas;
}

private renderChessboardMosaic(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  metrics?: ChessboardTileMetrics
): void {
  const tileMetrics = metrics || this.chessboardTileMetrics(this.activeSurface, undefined, pixelsPerMeter);
  const seamWidth = this.chessboardSeamWidthPx(tileMetrics.pixelsPerMeter);
  const seamColor = this.chessboardSeamColor(image);
  const module = tileMetrics.modulePx;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = seamColor;
  ctx.fillRect(0, 0, width, height);
  const planks = this.createOrientedPlankSources(image, tileMetrics.plankW, tileMetrics.modulePx);

  const rows = Math.ceil(height / Math.max(module, 1)) + 2;
  const cols = Math.ceil(width / Math.max(module, 1)) + 2;

  for (let row = -1; row <= rows; row += 1) {
    for (let col = -1; col <= cols; col += 1) {
      const horizontal = (row + col) % 2 !== 0;
      const x = col * module;
      const y = row * module;
      this.drawChessboardModule(ctx, planks, x, y, tileMetrics.modulePx, tileMetrics.plankW, tileMetrics.stripCount, seamWidth, horizontal);
    }
  }

  ctx.restore();
}

private chessboardTileMetrics(
  surface: TargetSurface,
  layer?: PreviewLayerInput,
  pixelsPerMeterOverride?: number
): ChessboardTileMetrics {
  const isRealScaleSurface = surface === 'floor' || surface === 'countertop';
  const roomWidth = this.clampNumber(Number(layer?.roomWidthM || this.roomWidthM || 4), 0.5, 30);
  const roomDepth = this.clampNumber(Number(layer?.roomDepthM || this.roomDepthM || 5), 0.5, 40);
  const requestedPpm = this.clampNumber(Number(pixelsPerMeterOverride || layer?.pixelsPerMeter || this.pixelsPerMeter || 900), 300, 1600);
  const productWidthM = Number(layer?.productWidthMm || 0) > 0
    ? Number(layer?.productWidthMm) / 1000
    : 0;
  const productHeightM = Number(layer?.productHeightMm || 0) > 0
    ? Number(layer?.productHeightMm) / 1000
    : 0;
  const widthFromSettingsM = Number(layer?.plankWidthM || this.plankWidthM || 0.184);
  const lengthFromSettingsM = Number(layer?.plankLengthM || this.plankLengthM || 1.22);
  const rawWidthM = Math.max(0.01, productWidthM || widthFromSettingsM);
  const rawLengthM = Math.max(0.01, productHeightM || lengthFromSettingsM);
  const realWidthM = Math.min(rawWidthM, rawLengthM);
  const realLengthM = Math.max(rawWidthM, rawLengthM);
  const legacySizeMm = Number(layer?.groutWidthM ?? this.groutWidthM ?? 0) * 1000;
  const groutEnabled = !!(layer?.groutEnabled ?? this.groutEnabled);
  const groutSizeMm = this.clampNumber(Number(layer?.groutSizeMm ?? this.groutSizeMm ?? legacySizeMm ?? 0), 0, 80);
  const seamWidthM = groutEnabled ? groutSizeMm / 1000 : 0;
  const roomLimitedPpm = isRealScaleSurface
    ? Math.min(requestedPpm, 4096 / Math.max(roomWidth, roomDepth, realLengthM * 2 + seamWidthM * 2))
    : requestedPpm;
  const ppm = Math.max(120, roomLimitedPpm);
  const seamWidthPx = groutEnabled ? this.clampNumber((groutSizeMm / 1000) * ppm, 1, 12) : 0;
  const plankW = Math.max(12, Math.round(realWidthM * ppm));
  const targetModulePx = Math.max(plankW, Math.round(realLengthM * ppm));
  const stripCount = Math.max(1, Math.round(targetModulePx / Math.max(plankW, 1)));
  const modulePx = Math.max(plankW, stripCount * plankW);
  const tileWidthPx = this.clampNumber(Math.round(modulePx * 2), 128, 4096);
  const tileHeightPx = this.clampNumber(Math.round(modulePx * 2), 128, 4096);

  return {
    tileWidthPx,
    tileHeightPx,
    tileWidthM: tileWidthPx / ppm,
    tileHeightM: tileHeightPx / ppm,
    pixelsPerMeter: ppm,
    plankW,
    modulePx,
    moduleM: modulePx / ppm,
    stripCount
  };
}

private chessboardTileMetricsForLayer(layer: PreviewLayerInput): ChessboardTileMetrics {
  return this.chessboardTileMetrics(layer.surface, layer);
}

private drawChessboardModule(
  ctx: CanvasRenderingContext2D,
  planks: { vertical: HTMLCanvasElement; horizontal: HTMLCanvasElement },
  x: number,
  y: number,
  size: number,
  stripWidth: number,
  stripCount: number,
  seamWidth: number,
  horizontal: boolean
): void {
  const stripPitch = stripWidth;
  const inset = seamWidth / 2;
  const longSize = Math.max(1, size - seamWidth);

  for (let index = 0; index < stripCount; index += 1) {
    const offset = index * stripPitch;
    const remaining = Math.max(0, size - offset);
    const thickness = Math.min(stripWidth, remaining) - seamWidth;
    if (thickness <= 0) {
      continue;
    }

    if (horizontal) {
      ctx.drawImage(planks.horizontal, x + inset, y + offset + inset, longSize, thickness);
    } else {
      ctx.drawImage(planks.vertical, x + offset + inset, y + inset, thickness, longSize);
    }
  }
}

private drawRotatedChessboardStrip(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();
  ctx.translate(x + width / 2, y + height / 2);
  ctx.rotate(Math.PI / 2);
  this.drawImageNoCrop(ctx, image, -height / 2, -width / 2, height, width);
  ctx.restore();
}

private chessboardSeamWidthPx(pixelsPerMeter: number): number {
  if (!this.groutEnabled) {
    return 0;
  }

  return Math.max(1, this.herringboneSeamWidthPx(pixelsPerMeter));
}

private chessboardSeamColor(image: CanvasImageSource): string {
  if (this.groutEnabled) {
    return String(this.groutColor || '#CD9A6C');
  }

  return this.averageImageColor(image);
}

private drawBasketweavePattern(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  surface: TargetSurface
): void {
  const tile = this.createBasketweaveTileCanvas(image, this.basketweaveTileMetrics(surface, undefined, pixelsPerMeter));
  const pattern = ctx.createPattern(tile, 'repeat');
  if (!pattern) {
    this.renderBasketweaveMosaic(ctx, image, width, height, pixelsPerMeter);
    return;
  }

  ctx.save();
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

private createBasketweaveMosaicTexture(image: CanvasImageSource, surface: TargetSurface, layer?: PreviewLayerInput): THREE.CanvasTexture {
  const canvas = this.createBasketweaveTileCanvas(image, this.basketweaveTileMetrics(surface, layer));

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
  texture.needsUpdate = true;
  return texture;
}

private createBasketweaveTileCanvas(image: CanvasImageSource, metrics: BasketweaveTileMetrics): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = metrics.tileWidthPx;
  canvas.height = metrics.tileHeightPx;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  this.renderBasketweaveMosaic(ctx, image, canvas.width, canvas.height, metrics.pixelsPerMeter, metrics);
  return canvas;
}

private renderBasketweaveMosaic(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  metrics?: BasketweaveTileMetrics
): void {
  const tileMetrics = metrics || this.basketweaveTileMetrics(this.activeSurface, undefined, pixelsPerMeter);
  const seamWidth = this.basketweaveSeamWidthPx(tileMetrics.pixelsPerMeter);
  const seamColor = this.basketweaveSeamColor(image);
  const module = tileMetrics.modulePx;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = seamColor;
  ctx.fillRect(0, 0, width, height);
  const planks = this.createOrientedPlankSources(image, tileMetrics.plankW, tileMetrics.modulePx);

  const rows = Math.ceil(height / Math.max(module, 1)) + 2;
  const cols = Math.ceil(width / Math.max(module, 1)) + 2;

  for (let row = -1; row <= rows; row += 1) {
    for (let col = -1; col <= cols; col += 1) {
      const horizontal = (row + col) % 2 !== 0;
      this.drawBasketweaveModule(
        ctx,
        planks,
        col * module,
        row * module,
        tileMetrics.modulePx,
        tileMetrics.plankW,
        tileMetrics.stripCount,
        seamWidth,
        horizontal
      );
    }
  }

  ctx.restore();
}

private basketweaveTileMetrics(
  surface: TargetSurface,
  layer?: PreviewLayerInput,
  pixelsPerMeterOverride?: number
): BasketweaveTileMetrics {
  const isRealScaleSurface = surface === 'floor' || surface === 'countertop';
  const roomWidth = this.clampNumber(Number(layer?.roomWidthM || this.roomWidthM || 4), 0.5, 30);
  const roomDepth = this.clampNumber(Number(layer?.roomDepthM || this.roomDepthM || 5), 0.5, 40);
  const requestedPpm = this.clampNumber(Number(pixelsPerMeterOverride || layer?.pixelsPerMeter || this.pixelsPerMeter || 900), 300, 1600);
  const productWidthM = Number(layer?.productWidthMm || 0) > 0
    ? Number(layer?.productWidthMm) / 1000
    : 0;
  const productHeightM = Number(layer?.productHeightMm || 0) > 0
    ? Number(layer?.productHeightMm) / 1000
    : 0;
  const widthFromSettingsM = Number(layer?.plankWidthM || this.plankWidthM || 0.184);
  const lengthFromSettingsM = Number(layer?.plankLengthM || this.plankLengthM || 1.22);
  const rawWidthM = Math.max(0.01, productWidthM || widthFromSettingsM);
  const rawLengthM = Math.max(0.01, productHeightM || lengthFromSettingsM);
  const realWidthM = Math.min(rawWidthM, rawLengthM);
  const legacySizeMm = Number(layer?.groutWidthM ?? this.groutWidthM ?? 0) * 1000;
  const groutEnabled = !!(layer?.groutEnabled ?? this.groutEnabled);
  const groutSizeMm = this.clampNumber(Number(layer?.groutSizeMm ?? this.groutSizeMm ?? legacySizeMm ?? 0), 0, 80);
  const seamWidthM = groutEnabled ? groutSizeMm / 1000 : 0;
  const stripCount = 2;
  const moduleM = Math.max(realWidthM * stripCount + seamWidthM, 0.04);
  const roomLimitedPpm = isRealScaleSurface
    ? Math.min(requestedPpm, 4096 / Math.max(roomWidth, roomDepth, moduleM * 2))
    : requestedPpm;
  const ppm = Math.max(120, roomLimitedPpm);
  const plankW = Math.max(12, Math.round(realWidthM * ppm));
  const modulePx = Math.max(plankW, stripCount * plankW);
  const tileWidthPx = this.clampNumber(Math.round(modulePx * 2), 128, 4096);
  const tileHeightPx = this.clampNumber(Math.round(modulePx * 2), 128, 4096);

  return {
    tileWidthPx,
    tileHeightPx,
    tileWidthM: tileWidthPx / ppm,
    tileHeightM: tileHeightPx / ppm,
    pixelsPerMeter: ppm,
    plankW,
    modulePx,
    moduleM: modulePx / ppm,
    stripCount
  };
}

private basketweaveTileMetricsForLayer(layer: PreviewLayerInput): BasketweaveTileMetrics {
  return this.basketweaveTileMetrics(layer.surface, layer);
}

private drawBasketweaveModule(
  ctx: CanvasRenderingContext2D,
  planks: { vertical: HTMLCanvasElement; horizontal: HTMLCanvasElement },
  x: number,
  y: number,
  size: number,
  stripWidth: number,
  stripCount: number,
  seamWidth: number,
  horizontal: boolean
): void {
  const stripPitch = stripWidth;
  const inset = seamWidth / 2;
  const longSize = Math.max(1, size - seamWidth);

  for (let index = 0; index < stripCount; index += 1) {
    const offset = index * stripPitch;
    const remaining = Math.max(0, size - offset);
    const thickness = Math.min(stripWidth, remaining) - seamWidth;
    if (thickness <= 0) {
      continue;
    }

    if (horizontal) {
      ctx.drawImage(planks.horizontal, x + inset, y + offset + inset, longSize, thickness);
    } else {
      ctx.drawImage(planks.vertical, x + offset + inset, y + inset, thickness, longSize);
    }
  }
}

private drawRotatedBasketweaveStrip(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();
  ctx.translate(x + width / 2, y + height / 2);
  ctx.rotate(Math.PI / 2);
  this.drawImageNoCrop(ctx, image, -height / 2, -width / 2, height, width);
  ctx.restore();
}

private basketweaveSeamWidthPx(pixelsPerMeter: number): number {
  if (!this.groutEnabled) {
    return 0;
  }

  return Math.max(1, this.herringboneSeamWidthPx(pixelsPerMeter));
}

private basketweaveSeamColor(image: CanvasImageSource): string {
  if (this.groutEnabled) {
    return String(this.groutColor || '#CD9A6C');
  }

  return this.averageImageColor(image);
}

 private drawStraightPattern(ctx: CanvasRenderingContext2D, image: CanvasImageSource, width: number, height: number, pixelsPerMeter: number): void {
  const realWidth = Math.max(0.01, Number(this.plankWidthM || 0.184));
  const realLength = Math.max(realWidth, Number(this.plankLengthM || 1.22));
  const plankL = Math.max(48, Math.round(realLength * pixelsPerMeter));
  const plankW = Math.max(12, Math.round(realWidth * pixelsPerMeter));
  const gap = this.patternGapPx(pixelsPerMeter);

  for (let y = -plankW; y < height + plankW; y += plankW + gap) {
    const row = Math.floor((y + plankW) / (plankW + gap));
    const offset =
      this.pattern === 'third'
        ? (row % 3) * (plankL / 3)
        : this.pattern === 'random'
          ? (((row * 37) % 100) / 100) * plankL
          : this.pattern === 'offset'
            ? (row % 2 === 0 ? 0 : plankL / 2)
            : 0;

    for (let x = -plankL - offset; x < width + plankL; x += plankL + gap) {
      this.drawTexturedRect(ctx, image, x + offset, y, plankL, plankW, 0, gap);
    }
  }
}

  private drawTilePattern(ctx: CanvasRenderingContext2D, image: CanvasImageSource, width: number, height: number, pixelsPerMeter: number): void {
    const tileW = Math.max(24, Math.round(Number(this.plankWidthM || 0.184) * pixelsPerMeter));
    const tileH = Math.max(24, Math.round(Number(this.plankLengthM || 1.22) * pixelsPerMeter));
    const gap = this.patternGapPx(pixelsPerMeter);
    for (let y = -tileH; y < height + tileH; y += tileH + gap) {
      for (let x = -tileW; x < width + tileW; x += tileW + gap) {
        this.drawTexturedRect(ctx, image, x, y, tileW, tileH, 0, gap);
      }
    }
  }

private drawHerringbonePattern(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  surface: TargetSurface
): void {
  const tile = this.createHerringboneTileCanvas(image, this.herringboneTileMetrics(surface, undefined, pixelsPerMeter));
  const pattern = ctx.createPattern(tile, 'repeat');
  if (!pattern) {
    this.renderHerringboneMosaic(ctx, image, width, height, pixelsPerMeter);
    return;
  }

  ctx.save();
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

private createHerringboneMosaicTexture(image: CanvasImageSource, surface: TargetSurface): THREE.CanvasTexture {
  const canvas = this.createHerringboneTileCanvas(image, this.herringboneTileMetrics(surface));

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
  texture.needsUpdate = true;
  return texture;
}

private createHerringboneTileCanvas(image: CanvasImageSource, metrics: HerringboneTileMetrics): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = metrics.tileSizePx;
  canvas.height = metrics.tileSizePx;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  this.renderHerringboneMosaic(ctx, image, canvas.width, canvas.height, metrics.pixelsPerMeter, metrics);
  return canvas;
}

private renderHerringboneMosaic(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  metrics?: HerringboneTileMetrics
): void {
  const tileMetrics = metrics || this.herringboneTileMetrics(this.activeSurface, undefined, pixelsPerMeter);
  const plankW = tileMetrics.plankW;
  const plankL = tileMetrics.plankL;
  const seamWidth = this.herringboneSeamWidthPx(pixelsPerMeter);
  const seamColor = this.herringboneSeamColor(image);
  const cells = this.buildHerringboneCells(width, height, plankW, plankL);

  this.validateHerringboneCells(cells);

  const planks = this.createHerringbonePlankSources(image, plankW, plankL);

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = seamColor;
  ctx.fillRect(0, 0, width, height);

  for (const cell of cells) {
    const source = cell.orientation === 1 ? planks.horizontal : planks.vertical;
    this.renderHerringboneCell(ctx, source, cell.points, seamWidth);
  }

  ctx.restore();
}

private herringboneTileMetrics(
  surface: TargetSurface,
  layer?: PreviewLayerInput,
  pixelsPerMeterOverride?: number
): HerringboneTileMetrics {
  const isRealScaleSurface = surface === 'floor' || surface === 'countertop';
  const roomWidth = this.clampNumber(Number(layer?.roomWidthM || this.roomWidthM || 4), 0.5, 30);
  const roomDepth = this.clampNumber(Number(layer?.roomDepthM || this.roomDepthM || 5), 0.5, 40);
  const requestedPpm = this.clampNumber(Number(pixelsPerMeterOverride || layer?.pixelsPerMeter || this.pixelsPerMeter || 900), 300, 1600);
  const roomLimitedPpm = isRealScaleSurface
    ? Math.min(requestedPpm, 4096 / Math.max(roomWidth, roomDepth))
    : requestedPpm;
  const realWidthM = Math.max(0.01, Number(layer?.plankWidthM || this.plankWidthM || 0.125));
  const realLengthM = Math.max(realWidthM * 2, Number(layer?.plankLengthM || this.plankLengthM || realWidthM * 5));
  const tileLimitedPpm = Math.min(roomLimitedPpm, 4096 / Math.max(Math.SQRT2 * realLengthM, 0.0001));
  const ppm = Math.max(120, tileLimitedPpm);
  const plankW = Math.max(24, Math.round(realWidthM * ppm));
  const plankL = Math.max(plankW * 2, Math.round(realLengthM * ppm));
  const lengthCells = Math.max(2, Math.round(plankL / Math.max(1, plankW)));
  const layoutPlankL = lengthCells * plankW;
  const basePeriodPx = Math.max(1, Math.SQRT2 * layoutPlankL);
  const repeatMultiplier = Math.max(1, Math.floor(4096 / basePeriodPx), Math.ceil(512 / basePeriodPx));
  const boundedMultiplier = Math.max(1, Math.min(repeatMultiplier, Math.floor(4096 / basePeriodPx) || 1));
  const tileSizePx = this.clampNumber(Math.round(basePeriodPx * boundedMultiplier), 256, 4096);

  return {
    tileSizePx,
    tileSizeM: tileSizePx / ppm,
    pixelsPerMeter: ppm,
    plankW,
    plankL
  };
}

private herringboneTileMetricsForLayer(layer: PreviewLayerInput): HerringboneTileMetrics {
  return this.herringboneTileMetrics(layer.surface, layer);
}

private drawChevronPattern(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  surface: TargetSurface
): void {
  const tile = this.createChevronTileCanvas(image, this.chevronTileMetrics(surface, undefined, pixelsPerMeter));
  const pattern = ctx.createPattern(tile, 'repeat');
  if (!pattern) {
    this.renderChevronMosaic(ctx, image, width, height, pixelsPerMeter);
    return;
  }

  ctx.save();
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

private createChevronMosaicTexture(image: CanvasImageSource, surface: TargetSurface): THREE.CanvasTexture {
  const canvas = this.createChevronTileCanvas(image, this.chevronTileMetrics(surface));

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.anisotropy = Math.min(16, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
  texture.needsUpdate = true;
  return texture;
}

private createChevronTileCanvas(image: CanvasImageSource, metrics: ChevronTileMetrics): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = metrics.tileWidthPx;
  canvas.height = metrics.tileHeightPx;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  this.renderChevronMosaic(ctx, image, canvas.width, canvas.height, metrics.pixelsPerMeter, metrics);
  return canvas;
}

private renderChevronMosaic(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  width: number,
  height: number,
  pixelsPerMeter: number,
  metrics?: ChevronTileMetrics
): void {
  const tileMetrics = metrics || this.chevronTileMetrics(this.activeSurface, undefined, pixelsPerMeter);
  const seamWidth = this.chevronSeamWidthPx(tileMetrics.pixelsPerMeter);
  const seamColor = this.chevronSeamColor(image);
  const columnW = tileMetrics.columnW;
  const halfW = tileMetrics.halfColumnW;
  const chevronPlanks: ChevronPlankPolygon[] = [];

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = this.averageImageColor(image);
  ctx.fillRect(0, 0, width, height);
  const plankSources = this.createOrientedPlankSources(image, tileMetrics.plankW, tileMetrics.plankL);

  for (let columnX = -columnW; columnX <= width + columnW; columnX += columnW) {
    const seamX = columnX + halfW;
    chevronPlanks.push(
      ...this.buildChevronHalfPlanks(tileMetrics, columnX, seamX, 0, height, seamX, 'left'),
      ...this.buildChevronHalfPlanks(tileMetrics, seamX, columnX + columnW, 0, height, seamX, 'right')
    );
  }

  for (const plank of chevronPlanks) {
    this.renderChevronPlank(ctx, plankSources.horizontal, tileMetrics, plank);
  }

  ctx.strokeStyle = seamColor;
  ctx.lineWidth = Math.max(0.75, seamWidth * 0.55);
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';

  for (const plank of chevronPlanks) {
    this.strokeChevronPolygon(ctx, plank.points, width, height);
  }

  ctx.restore();
}

private chevronTileMetrics(
  surface: TargetSurface,
  layer?: PreviewLayerInput,
  pixelsPerMeterOverride?: number
): ChevronTileMetrics {
  const isRealScaleSurface = surface === 'floor' || surface === 'countertop';
  const roomWidth = this.clampNumber(Number(layer?.roomWidthM || this.roomWidthM || 4), 0.5, 30);
  const roomDepth = this.clampNumber(Number(layer?.roomDepthM || this.roomDepthM || 5), 0.5, 40);
  const requestedPpm = this.clampNumber(Number(pixelsPerMeterOverride || layer?.pixelsPerMeter || this.pixelsPerMeter || 900), 300, 1600);
  const realWidthM = Math.max(0.01, Number(layer?.plankWidthM || this.plankWidthM || 0.125));
  const realLengthM = Math.max(realWidthM * 2, Number(layer?.plankLengthM || this.plankLengthM || realWidthM * 5));
  const roomLimitedPpm = isRealScaleSurface
    ? Math.min(requestedPpm, 4096 / Math.max(roomWidth, roomDepth))
    : requestedPpm;
  const cutLengthM = Math.max(realWidthM, realLengthM - realWidthM);
  const columnWidthM = Math.SQRT2 * cutLengthM;
  const ppm = Math.max(120, Math.min(roomLimitedPpm, 4096 / Math.max(columnWidthM, 0.0001)));
  const plankW = Math.max(24, Math.round(realWidthM * ppm));
  const plankL = Math.max(plankW * 2, Math.round(realLengthM * ppm));
  const cutPlankL = Math.max(plankW, plankL - plankW);
  const halfColumnW = Math.max(plankW, Math.round(cutPlankL / Math.SQRT2));
  const columnW = Math.min(4096, Math.max(halfColumnW * 2, 128));
  const rowStep = Math.max(4, Math.round(plankW / Math.SQRT1_2));
  const tileRows = Math.max(2, Math.ceil(columnW / rowStep));
  const tileHeightPx = Math.min(4096, Math.max(rowStep, tileRows * rowStep));

  return {
    tileWidthPx: columnW,
    tileHeightPx,
    tileWidthM: columnW / ppm,
    tileHeightM: tileHeightPx / ppm,
    pixelsPerMeter: ppm,
    plankW,
    plankL,
    cutPlankL,
    halfColumnW,
    columnW,
    rowStep
  };
}

private chevronTileMetricsForLayer(layer: PreviewLayerInput): ChevronTileMetrics {
  return this.chevronTileMetrics(layer.surface, layer);
}

private buildChevronHalfPlanks(
  metrics: ChevronTileMetrics,
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
  seamX: number,
  side: 'left' | 'right'
): ChevronPlankPolygon[] {
  const rect = [
    { x: xMin, y: yMin },
    { x: xMax, y: yMin },
    { x: xMax, y: yMax },
    { x: xMin, y: yMax }
  ];
  const planks: ChevronPlankPolygon[] = [];
  const pad = metrics.halfColumnW + metrics.rowStep * 4;
  const startY = Math.floor((yMin - pad) / metrics.rowStep) * metrics.rowStep;
  const endY = yMax + pad;
  const value = side === 'left'
    ? (point: Point2D) => point.y - point.x
    : (point: Point2D) => point.y + point.x;

  for (let y0 = startY; y0 < endY; y0 += metrics.rowStep) {
    const y1 = y0 + metrics.rowStep;
    const yMid = (y0 + y1) / 2;
    const min = side === 'left' ? y0 - seamX : y0 + seamX;
    const max = side === 'left' ? y1 - seamX : y1 + seamX;
    const polygon = this.clipPolygonByLinearRange(rect, value, min, max);

    if (polygon.length >= 3 && this.polygonArea(polygon) > 0.5) {
      const rotation = side === 'left' ? Math.PI / 4 : -Math.PI / 4;
      const halfVisibleX = Math.cos(rotation) * metrics.cutPlankL * 0.5;
      const halfVisibleY = Math.sin(rotation) * metrics.cutPlankL * 0.5;
      const center = side === 'left'
        ? { x: seamX - halfVisibleX, y: yMid - halfVisibleY }
        : { x: seamX + halfVisibleX, y: yMid + halfVisibleY };

      planks.push({ points: polygon, side, center });
    }
  }

  return planks;
}

private renderChevronPlank(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  metrics: ChevronTileMetrics,
  plank: ChevronPlankPolygon
): void {
  const rotation = plank.side === 'left' ? Math.PI / 4 : -Math.PI / 4;
  const sourceW = Math.max(1, metrics.plankL);
  const sourceH = Math.max(1, metrics.plankW);

  ctx.save();
  this.createPolygonPath(ctx, plank.points);
  ctx.clip();
  ctx.translate(plank.center.x, plank.center.y);
  ctx.rotate(rotation);
  ctx.drawImage(source, -sourceW / 2, -sourceH / 2, sourceW, sourceH);

  ctx.restore();
}

private clipPolygonByLinearRange(
  polygon: Point2D[],
  value: (point: Point2D) => number,
  min: number,
  max: number
): Point2D[] {
  return this.clipPolygonByLinearValue(
    this.clipPolygonByLinearValue(polygon, value, min, true),
    value,
    max,
    false
  );
}

private clipPolygonByLinearValue(
  polygon: Point2D[],
  value: (point: Point2D) => number,
  limit: number,
  keepGreater: boolean
): Point2D[] {
  if (polygon.length === 0) {
    return polygon;
  }

  const clipped: Point2D[] = [];
  const isInside = (point: Point2D) => {
    const signed = value(point) - limit;
    return keepGreater ? signed >= -0.001 : signed <= 0.001;
  };

  for (let i = 0; i < polygon.length; i += 1) {
    const current = polygon[i];
    const previous = polygon[(i + polygon.length - 1) % polygon.length];
    const currentInside = isInside(current);
    const previousInside = isInside(previous);

    if (currentInside !== previousInside) {
      const previousValue = value(previous);
      const currentValue = value(current);
      const denominator = currentValue - previousValue;
      const t = Math.abs(denominator) <= 0.000001 ? 0 : (limit - previousValue) / denominator;
      clipped.push({
        x: previous.x + (current.x - previous.x) * t,
        y: previous.y + (current.y - previous.y) * t
      });
    }

    if (currentInside) {
      clipped.push({ ...current });
    }
  }

  return clipped;
}

private polygonArea(points: Point2D[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }

  return Math.abs(area) / 2;
}

private createPolygonPath(ctx: CanvasRenderingContext2D, points: Point2D[]): void {
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.closePath();
}

private strokeChevronPolygon(ctx: CanvasRenderingContext2D, points: Point2D[], tileW: number, tileH: number): void {
  const edgeEpsilon = 0.75;
  const isTileRepeatEdge = (a: Point2D, b: Point2D) => {
    const onTop = Math.abs(a.y) <= edgeEpsilon && Math.abs(b.y) <= edgeEpsilon;
    const onBottom = Math.abs(a.y - tileH) <= edgeEpsilon && Math.abs(b.y - tileH) <= edgeEpsilon;
    const onLeft = Math.abs(a.x) <= edgeEpsilon && Math.abs(b.x) <= edgeEpsilon;
    const onRight = Math.abs(a.x - tileW) <= edgeEpsilon && Math.abs(b.x - tileW) <= edgeEpsilon;
    return onTop || onBottom || onLeft || onRight;
  };

  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    if (isTileRepeatEdge(current, next)) {
      continue;
    }

    ctx.beginPath();
    ctx.moveTo(current.x, current.y);
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
  }
}

private chevronSeamWidthPx(pixelsPerMeter: number): number {
  if (this.groutEnabled) {
    return Math.max(1, this.herringboneSeamWidthPx(pixelsPerMeter));
  }

  return this.clampNumber(this.patternGapPx(pixelsPerMeter), 1, 10);
}

private chevronSeamColor(image: CanvasImageSource): string {
  if (this.groutEnabled) {
    return String(this.groutColor || '#CD9A6C');
  }

  const average = this.averageImageColor(image);
  const channels = average.match(/\d+/g)?.map((value) => Number(value)) || [];
  if (channels.length >= 3) {
    const darken = 0.56;
    return `rgb(${Math.round(channels[0] * darken)}, ${Math.round(channels[1] * darken)}, ${Math.round(channels[2] * darken)})`;
  }

  return '#6f5441';
}

private buildHerringboneCells(width: number, height: number, plankW: number, plankL: number): HerringboneCell[] {
  const lengthCells = Math.max(2, Math.round(plankL / Math.max(1, plankW)));
  const layoutPlankL = lengthCells * plankW;
  const workSize = Math.ceil(Math.sqrt(2) * Math.max(width, height)) + layoutPlankL * 4;
  const workCells = Math.ceil(workSize / plankW) + lengthCells * 8;
  const offsetCells = Math.floor(workCells / 2);
  const limit = workCells + lengthCells;
  const theta = Math.PI / 4;
  const cosT = Math.cos(theta);
  const sinT = Math.sin(theta);
  const workCenter = { x: workSize / 2, y: workSize / 2 };
  const canvasCenter = { x: width / 2, y: height / 2 };
  const origin = {
    x: (workCells * plankW - workSize) / 2,
    y: (workCells * plankW - workSize) / 2
  };
  const cells: HerringboneCell[] = [];

  const rotateToCanvas = (points: Point2D[]): Point2D[] => points.map((point) => {
    const localX = point.x - origin.x - workCenter.x;
    const localY = point.y - origin.y - workCenter.y;
    return {
      x: localX * cosT - localY * sinT + canvasCenter.x,
      y: localX * sinT + localY * cosT + canvasCenter.y
    };
  });

  for (let m = -limit; m <= limit; m += 1) {
    for (let k = -limit; k <= limit; k += 1) {
      const cellX = m * lengthCells - k + offsetCells;
      const cellY = m * lengthCells + k + offsetCells;
      if (cellX < -lengthCells || cellX > workCells + lengthCells || cellY < -lengthCells || cellY > workCells + lengthCells) {
        continue;
      }

      const x = cellX * plankW;
      const y = cellY * plankW;
      const horizontal = rotateToCanvas([
        { x, y },
        { x: x + layoutPlankL, y },
        { x: x + layoutPlankL, y: y + plankW },
        { x, y: y + plankW }
      ]);

      const vx = (cellX + lengthCells) * plankW;
      const vertical = rotateToCanvas([
        { x: vx, y },
        { x: vx + plankW, y },
        { x: vx + plankW, y: y + layoutPlankL },
        { x: vx, y: y + layoutPlankL }
      ]);

      if (this.polygonTouchesCanvas(horizontal, width, height, plankW)) {
        cells.push({ points: horizontal, orientation: 1 });
      }

      if (this.polygonTouchesCanvas(vertical, width, height, plankW)) {
        cells.push({ points: vertical, orientation: 2 });
      }
    }
  }

  return cells;
}

private polygonTouchesCanvas(points: Point2D[], width: number, height: number, margin: number): boolean {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  return Math.max(...xs) >= -margin &&
    Math.min(...xs) <= width + margin &&
    Math.max(...ys) >= -margin &&
    Math.min(...ys) <= height + margin;
}

private validateHerringboneCells(cells: HerringboneCell[]): void {
  for (let i = 0; i < cells.length; i += 1) {
    for (let j = i + 1; j < cells.length; j += 1) {
      if (this.polygonsOverlapWithArea(cells[i].points, cells[j].points)) {
        throw new Error('Herringbone layout invalid: plank polygons overlap');
      }
    }
  }
}

private polygonsOverlapWithArea(a: Point2D[], b: Point2D[]): boolean {
  const axes: Point2D[] = [];
  const collectAxes = (poly: Point2D[]) => {
    for (let i = 0; i < poly.length; i += 1) {
      const p1 = poly[i];
      const p2 = poly[(i + 1) % poly.length];
      const edge = { x: p2.x - p1.x, y: p2.y - p1.y };
      const length = Math.hypot(edge.x, edge.y);
      if (length > 0.0001) {
        axes.push({ x: -edge.y / length, y: edge.x / length });
      }
    }
  };

  collectAxes(a);
  collectAxes(b);

  for (const axis of axes) {
    const pa = this.projectPolygon(a, axis);
    const pb = this.projectPolygon(b, axis);
    if (pa.max <= pb.min + 0.01 || pb.max <= pa.min + 0.01) {
      return false;
    }
  }

  return true;
}

private projectPolygon(points: Point2D[], axis: Point2D): { min: number; max: number } {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    const value = point.x * axis.x + point.y * axis.y;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }

  return { min, max };
}

private createOrientedPlankSources(
  image: CanvasImageSource,
  plankW: number,
  plankL: number
): { vertical: HTMLCanvasElement; horizontal: HTMLCanvasElement } {
  const vertical = document.createElement('canvas');
  vertical.width = plankW;
  vertical.height = plankL;
  const verticalCtx = vertical.getContext('2d');
  if (verticalCtx) {
    verticalCtx.imageSmoothingEnabled = true;
    verticalCtx.imageSmoothingQuality = 'high';
    verticalCtx.drawImage(image, 0, 0, plankW, plankL);
  }

  const horizontal = document.createElement('canvas');
  horizontal.width = plankL;
  horizontal.height = plankW;
  const horizontalCtx = horizontal.getContext('2d');
  if (horizontalCtx) {
    horizontalCtx.imageSmoothingEnabled = true;
    horizontalCtx.imageSmoothingQuality = 'high';
    horizontalCtx.translate(plankL / 2, plankW / 2);
    horizontalCtx.rotate(-Math.PI / 2);
    horizontalCtx.drawImage(vertical, -plankW / 2, -plankL / 2);
  }

  return { vertical, horizontal };
}

private createHerringbonePlankSources(
  image: CanvasImageSource,
  plankW: number,
  plankL: number
): { vertical: HTMLCanvasElement; horizontal: HTMLCanvasElement } {
  return this.createOrientedPlankSources(image, plankW, plankL);
}

private renderHerringboneCell(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  polygon: Point2D[],
  seamWidth: number
): void {
  const points = this.shrinkPolygon(polygon, seamWidth > 0 ? seamWidth : 0.35);
  const p0 = points[0];
  const p1 = points[1];
  const p3 = points[3];
  const sourceW = Math.max(1, source.width);
  const sourceH = Math.max(1, source.height);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.closePath();
  ctx.clip();
  ctx.setTransform(
    (p1.x - p0.x) / sourceW,
    (p1.y - p0.y) / sourceW,
    (p3.x - p0.x) / sourceH,
    (p3.y - p0.y) / sourceH,
    p0.x,
    p0.y
  );
  ctx.drawImage(source, 0, 0);
  ctx.restore();
}

private shrinkPolygon(points: Point2D[], pixels: number): Point2D[] {
  if (pixels <= 0) {
    return points.map((point) => ({ ...point }));
  }

  const center = points.reduce(
    (acc, point) => ({ x: acc.x + point.x / points.length, y: acc.y + point.y / points.length }),
    { x: 0, y: 0 }
  );

  return points.map((point) => {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    const length = Math.hypot(dx, dy);
    if (length <= 0.001) {
      return { ...point };
    }

    const scale = Math.max(0, (length - pixels) / length);
    return {
      x: center.x + dx * scale,
      y: center.y + dy * scale
    };
  });
}

private herringboneSeamWidthPx(pixelsPerMeter: number): number {
  const legacySizeMm = Number(this.groutWidthM || 0) * 1000;
  const sizeMm = this.clampNumber(Number(this.groutSizeMm || legacySizeMm || 0), 0, 80);
  if (!this.groutEnabled || sizeMm <= 0) {
    return 0;
  }

  return this.clampNumber((sizeMm / 1000) * pixelsPerMeter, 0, 12);
}

private herringboneSeamColor(image: CanvasImageSource): string {
  if (this.groutEnabled) {
    return String(this.groutColor || '#CD9A6C');
  }

  return this.averageImageColor(image);
}

private averageImageColor(image: CanvasImageSource): string {
  const sample = document.createElement('canvas');
  sample.width = 12;
  sample.height = 12;
  const sampleCtx = sample.getContext('2d', { willReadFrequently: true });
  if (!sampleCtx) {
    return '#b98a68';
  }

  sampleCtx.drawImage(image, 0, 0, sample.width, sample.height);
  const data = sampleCtx.getImageData(0, 0, sample.width, sample.height).data;
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    count += 1;
  }

  if (count <= 0) {
    return '#b98a68';
  }

  return `rgb(${Math.round(r / count)}, ${Math.round(g / count)}, ${Math.round(b / count)})`;
}

private drawImageCover(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  const source = image as { width?: number; height?: number; videoWidth?: number; videoHeight?: number };
  const sourceWidth = Number(source.width || source.videoWidth || width || 1);
  const sourceHeight = Number(source.height || source.videoHeight || height || 1);

  const sourceRatio = sourceWidth / sourceHeight;
  const targetRatio = width / height;

  let sx = 0;
  let sy = 0;
  let sw = sourceWidth;
  let sh = sourceHeight;

  if (sourceRatio > targetRatio) {
    sw = sourceHeight * targetRatio;
    sx = (sourceWidth - sw) / 2;
  } else {
    sh = sourceWidth / targetRatio;
    sy = (sourceHeight - sh) / 2;
  }

  ctx.drawImage(
    image,
    sx,
    sy,
    sw,
    sh,
    x,
    y,
    width,
    height
  );
}

private drawTexturedRect(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number,
  rotation: number,
  gap: number
): void {
  ctx.save();

  ctx.translate(x + width / 2, y + height / 2);
  ctx.rotate(rotation);

  const radius = Math.min(3, height * 0.12);

  ctx.beginPath();
  ctx.moveTo(-width / 2 + radius, -height / 2);
  ctx.lineTo(width / 2 - radius, -height / 2);
  ctx.quadraticCurveTo(width / 2, -height / 2, width / 2, -height / 2 + radius);
  ctx.lineTo(width / 2, height / 2 - radius);
  ctx.quadraticCurveTo(width / 2, height / 2, width / 2 - radius, height / 2);
  ctx.lineTo(-width / 2 + radius, height / 2);
  ctx.quadraticCurveTo(-width / 2, height / 2, -width / 2, height / 2 - radius);
  ctx.lineTo(-width / 2, -height / 2 + radius);
  ctx.quadraticCurveTo(-width / 2, -height / 2, -width / 2 + radius, -height / 2);
  ctx.closePath();
  ctx.clip();

  // draw texture slightly larger to avoid white edges
  ctx.drawImage(
    image,
    -width / 2 - 2,
    -height / 2 - 2,
    width + 4,
    height + 4
  );

  ctx.restore();

  if (gap > 0) {
    ctx.save();
    ctx.translate(x + width / 2, y + height / 2);
    ctx.rotate(rotation);

    ctx.strokeStyle = 'rgba(24, 20, 16, 0.28)';
    ctx.lineWidth = Math.max(1, Math.min(gap, 2.5));
    ctx.strokeRect(-width / 2, -height / 2, width, height);

    ctx.restore();
  }
}

private patternGapPx(size: number): number {
  const realGap = Number(this.groutWidthM || 0.0015);
  return this.clampNumber(realGap * size, 1, 8);
}

private applyPatternTransform(texture: THREE.Texture): void {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, 1);
    texture.rotation = 0;
    texture.center.set(0, 0);
    texture.offset.set(0, 0);
    texture.needsUpdate = true;

    if (!this.previewMaterial) {
      return;
    }

    const uniforms = this.previewMaterial.uniforms;
    uniforms['patternMap'].value = texture;
    uniforms['repeatScale'].value = this.previewRepeatScale();
    if (uniforms['repeatScaleVec']) {
      const repeatScaleVec = uniforms['repeatScaleVec'].value as THREE.Vector2;
      const nextRepeatScaleVec = this.singlePreviewRepeatScaleVec();
      if (repeatScaleVec?.copy) {
        repeatScaleVec.copy(nextRepeatScaleVec);
      } else {
        uniforms['repeatScaleVec'].value = nextRepeatScaleVec;
      }
    }
    const singleLayerForGrid: PreviewLayerInput = {
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: '',
      maskUrl: '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      pattern: this.pattern,
      textureScale: this.textureScale,
      rotationAngle: this.textureRotation
    };
    if (uniforms['gridPhysicalRotation']) {
      uniforms['gridPhysicalRotation'].value = this.gridPhysicalRotationEnabledForLayer(singleLayerForGrid) ? 1 : 0;
    }
    if (uniforms['floorSurfaceSizeM']) {
      const floorSurfaceSizeM = uniforms['floorSurfaceSizeM'].value as THREE.Vector2;
      const nextFloorSurfaceSizeM = this.floorSurfaceSizeVecForLayer(singleLayerForGrid);
      if (floorSurfaceSizeM?.copy) {
        floorSurfaceSizeM.copy(nextFloorSurfaceSizeM);
      } else {
        uniforms['floorSurfaceSizeM'].value = nextFloorSurfaceSizeM;
      }
    }
    if (uniforms['useProjectiveUv']) {
      uniforms['useProjectiveUv'].value = this.shouldUseProjectiveUvForLayer({
        id: 'single-preview',
        surface: this.activeSurface,
        textureUrl: '',
        maskUrl: '',
        perspectivePoints: this.getEditablePointsOrDefault(),
        rotationAngle: this.textureRotation
      }) ? 1 : 0;
    }
    if (uniforms['projectiveUvMatrix']) {
      (uniforms['projectiveUvMatrix'].value as THREE.Matrix3).copy(
        this.projectiveUvMatrixForLayer({
          id: 'single-preview',
          surface: this.activeSurface,
          textureUrl: '',
          maskUrl: '',
          perspectivePoints: this.getEditablePointsOrDefault(),
          rotationAngle: this.textureRotation
        })
      );
    }
    uniforms['rotationRad'].value = this.patternRotationRadForLayer({
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: '',
      maskUrl: '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      rotationAngle: this.textureRotation
    });

    const offset = uniforms['offset'].value as THREE.Vector2;
    if (offset?.set) {
      offset.set(Number(this.textureOffsetX || 0), Number(this.textureOffsetY || 0));
    } else {
      uniforms['offset'].value = new THREE.Vector2(Number(this.textureOffsetX || 0), Number(this.textureOffsetY || 0));
    }

    const groutLayer: PreviewLayerInput = {
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: this.previewTextureUrl || '',
      maskUrl: this.maskImageUrl || '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      pattern: this.pattern,
      textureScale: this.textureScale,
      rotationAngle: this.textureRotation,
      plankWidthM: this.plankWidthM,
      plankLengthM: this.plankLengthM,
      groutWidthM: this.groutWidthM,
      groutEnabled: this.groutEnabled,
      groutSizeMm: this.groutSizeMm,
      groutColor: this.groutColor
    };
    if (uniforms['groutEnabled']) {
      uniforms['groutEnabled'].value = this.groutEnabledForLayer(groutLayer) ? 1 : 0;
    }
    if (uniforms['groutLineSize']) {
      const groutLineSize = uniforms['groutLineSize'].value as THREE.Vector2;
      const nextGroutLineSize = this.groutLineSizeForLayer(groutLayer);
      if (groutLineSize?.copy) {
        groutLineSize.copy(nextGroutLineSize);
      } else {
        uniforms['groutLineSize'].value = nextGroutLineSize;
      }
    }
    if (uniforms['groutColor']) {
      const groutColor = uniforms['groutColor'].value as THREE.Color;
      const nextGroutColor = this.groutColorForLayer(groutLayer);
      if (groutColor?.copy) {
        groutColor.copy(nextGroutColor);
      } else {
        uniforms['groutColor'].value = nextGroutColor;
      }
    }

    this.previewMaterial.needsUpdate = true;
  }

  private previewRepeatScale(): number {
    const scale = this.clampNumber(Number(this.textureScale || 1), 0.15, 5);

    // Herringbone/parquet biasanya sudah punya motif detail di texture aslinya.
    // Jangan terlalu sering di-repeat, supaya pattern tidak terlihat kecil/ramai.
    if (this.pattern === 'herringbone' || this.pattern === 'chevron') {
      return scale;
    }

    if (this.activeSurface === 'floor' || this.activeSurface === 'countertop') {
      return scale;
    }

    return scale * 1.8;
  }

  private singlePreviewRepeatScaleVec(): THREE.Vector2 {
    const layer: PreviewLayerInput = {
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: this.previewTextureUrl || '',
      maskUrl: this.maskImageUrl || '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      pattern: this.pattern,
      textureScale: this.textureScale,
      rotationAngle: this.textureRotation
    };

    if (
      this.shouldUseFloorProjectiveLayer(layer) ||
      this.shouldUseCeilingProjectiveLayer(layer) ||
      layer.surface === 'wall'
    ) {
      return this.previewRepeatScaleVecForLayer(layer);
    }

    const repeat = this.previewRepeatScale();
    return new THREE.Vector2(repeat, repeat);
  }

private applyRuntimeUniforms(): void {
  const isInteractiveMode =
    this.mode === 'preview' ||
    this.mode === 'compare';

  const isResultMode =
    this.mode === 'result';

  if (this.resultMaterial && this.resultMesh) {
    const compare = this.mode === 'compare'
      ? this.clampNumber(this.comparePosition, 0, 1)
      : 1;

    this.resultMaterial.uniforms['compareX'].value = compare;
    this.resultMaterial.uniforms['opacity'].value = this.resultOpacity;

    // Result final hanya muncul di mode result atau compare.
    // Jangan muncul di preview/interaktif.
    this.resultMesh.visible =
      !!this.resultImageUrl &&
      (this.mode === 'result' || this.mode === 'compare');

    this.resultMaterial.needsUpdate = true;
  }

  if (this.previewMesh && this.previewMaterial) {
    // Preview interaktif muncul di mode preview dan compare.
    // Ini yang bikin scale/rotasi/offset tetap jalan sebelum generate.
    this.previewMesh.visible =
      this.previewLayers.length === 0 &&
      !!this.previewTextureUrl &&
      !!this.perspectivePoints &&
      isInteractiveMode;

    const uniforms = this.previewMaterial.uniforms;

    uniforms['opacity'].value = this.previewOpacity;
    uniforms['maskMap'].value = this.maskTexture;
    uniforms['hasMask'].value = this.maskTexture ? 1 : 0;
    uniforms['imageWidth'].value = this.imageWidth;
    uniforms['imageHeight'].value = this.imageHeight;
    uniforms['compareX'].value = this.clampNumber(this.comparePosition, 0, 1);
    uniforms['isCompare'].value = this.mode === 'compare' ? 1 : 0;
    uniforms['repeatScale'].value = this.previewRepeatScale();
    if (uniforms['repeatScaleVec']) {
      const repeatScaleVec = uniforms['repeatScaleVec'].value as THREE.Vector2;
      const nextRepeatScaleVec = this.singlePreviewRepeatScaleVec();
      if (repeatScaleVec?.copy) {
        repeatScaleVec.copy(nextRepeatScaleVec);
      } else {
        uniforms['repeatScaleVec'].value = nextRepeatScaleVec;
      }
    }
    const singleLayerForGrid: PreviewLayerInput = {
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: '',
      maskUrl: '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      pattern: this.pattern,
      textureScale: this.textureScale,
      rotationAngle: this.textureRotation
    };
    if (uniforms['gridPhysicalRotation']) {
      uniforms['gridPhysicalRotation'].value = this.gridPhysicalRotationEnabledForLayer(singleLayerForGrid) ? 1 : 0;
    }
    if (uniforms['floorSurfaceSizeM']) {
      const floorSurfaceSizeM = uniforms['floorSurfaceSizeM'].value as THREE.Vector2;
      const nextFloorSurfaceSizeM = this.floorSurfaceSizeVecForLayer(singleLayerForGrid);
      if (floorSurfaceSizeM?.copy) {
        floorSurfaceSizeM.copy(nextFloorSurfaceSizeM);
      } else {
        uniforms['floorSurfaceSizeM'].value = nextFloorSurfaceSizeM;
      }
    }
    if (uniforms['useProjectiveUv']) {
      uniforms['useProjectiveUv'].value = this.shouldUseProjectiveUvForLayer({
        id: 'single-preview',
        surface: this.activeSurface,
        textureUrl: '',
        maskUrl: '',
        perspectivePoints: this.getEditablePointsOrDefault(),
        rotationAngle: this.textureRotation
      }) ? 1 : 0;
    }
    if (uniforms['projectiveUvMatrix']) {
      (uniforms['projectiveUvMatrix'].value as THREE.Matrix3).copy(
        this.projectiveUvMatrixForLayer({
          id: 'single-preview',
          surface: this.activeSurface,
          textureUrl: '',
          maskUrl: '',
          perspectivePoints: this.getEditablePointsOrDefault(),
          rotationAngle: this.textureRotation
        })
      );
    }
    uniforms['rotationRad'].value = this.patternRotationRadForLayer({
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: '',
      maskUrl: '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      rotationAngle: this.textureRotation
    });

    const offset = uniforms['offset'].value as THREE.Vector2;

    if (offset?.set) {
      offset.set(
        Number(this.textureOffsetX || 0),
        Number(this.textureOffsetY || 0)
      );
    } else {
      uniforms['offset'].value = new THREE.Vector2(
        Number(this.textureOffsetX || 0),
        Number(this.textureOffsetY || 0)
      );
    }

    const groutLayer: PreviewLayerInput = {
      id: 'single-preview',
      surface: this.activeSurface,
      textureUrl: this.previewTextureUrl || '',
      maskUrl: this.maskImageUrl || '',
      perspectivePoints: this.getEditablePointsOrDefault(),
      pattern: this.pattern,
      textureScale: this.textureScale,
      rotationAngle: this.textureRotation,
      plankWidthM: this.plankWidthM,
      plankLengthM: this.plankLengthM,
      groutWidthM: this.groutWidthM,
      groutEnabled: this.groutEnabled,
      groutSizeMm: this.groutSizeMm,
      groutColor: this.groutColor
    };
    if (uniforms['groutEnabled']) {
      uniforms['groutEnabled'].value = this.groutEnabledForLayer(groutLayer) ? 1 : 0;
    }
    if (uniforms['groutLineSize']) {
      const groutLineSize = uniforms['groutLineSize'].value as THREE.Vector2;
      const nextGroutLineSize = this.groutLineSizeForLayer(groutLayer);
      if (groutLineSize?.copy) {
        groutLineSize.copy(nextGroutLineSize);
      } else {
        uniforms['groutLineSize'].value = nextGroutLineSize;
      }
    }
    if (uniforms['groutColor']) {
      const groutColor = uniforms['groutColor'].value as THREE.Color;
      const nextGroutColor = this.groutColorForLayer(groutLayer);
      if (groutColor?.copy) {
        groutColor.copy(nextGroutColor);
      } else {
        uniforms['groutColor'].value = nextGroutColor;
      }
    }

    this.previewMaterial.needsUpdate = true;
  }

  for (let index = 0; index < this.previewLayerMeshes.length; index += 1) {
    const mesh = this.previewLayerMeshes[index];
    const material = this.previewLayerMaterials[index];

    mesh.visible = this.previewLayers.length > 0 && isInteractiveMode;

    if (material) {
      const layer = this.previewLayers[index];
      material.uniforms['opacity'].value = Number(layer?.opacity ?? this.previewOpacity ?? 0.82);
      if (layer && material.uniforms['useProjectiveUv']) {
        material.uniforms['useProjectiveUv'].value = this.shouldUseProjectiveUvForLayer(layer) ? 1 : 0;
      }
      if (layer && material.uniforms['projectiveUvMatrix']) {
        (material.uniforms['projectiveUvMatrix'].value as THREE.Matrix3).copy(this.projectiveUvMatrixForLayer(layer));
      }
      if (layer && material.uniforms['repeatScaleVec']) {
        const repeatScaleVec = material.uniforms['repeatScaleVec'].value as THREE.Vector2;
        const nextRepeatScaleVec = this.previewRepeatScaleVecForLayer(layer);
        if (repeatScaleVec?.copy) {
          repeatScaleVec.copy(nextRepeatScaleVec);
        } else {
          material.uniforms['repeatScaleVec'].value = nextRepeatScaleVec;
        }
      }
      if (layer && material.uniforms['gridPhysicalRotation']) {
        material.uniforms['gridPhysicalRotation'].value = this.gridPhysicalRotationEnabledForLayer(layer) ? 1 : 0;
      }
      if (layer && material.uniforms['floorSurfaceSizeM']) {
        const floorSurfaceSizeM = material.uniforms['floorSurfaceSizeM'].value as THREE.Vector2;
        const nextFloorSurfaceSizeM = this.floorSurfaceSizeVecForLayer(layer);
        if (floorSurfaceSizeM?.copy) {
          floorSurfaceSizeM.copy(nextFloorSurfaceSizeM);
        } else {
          material.uniforms['floorSurfaceSizeM'].value = nextFloorSurfaceSizeM;
        }
      }
      if (layer && material.uniforms['wallBboxMin'] && material.uniforms['wallBboxSize']) {
        const wallBbox = this.wallBboxUvForLayer(layer);
        const wallBboxMin = material.uniforms['wallBboxMin'].value as THREE.Vector2;
        const wallBboxSize = material.uniforms['wallBboxSize'].value as THREE.Vector2;
        if (wallBboxMin?.copy) {
          wallBboxMin.copy(wallBbox.min);
        } else {
          material.uniforms['wallBboxMin'].value = wallBbox.min;
        }
        if (wallBboxSize?.copy) {
          wallBboxSize.copy(wallBbox.size);
        } else {
          material.uniforms['wallBboxSize'].value = wallBbox.size;
        }
      }
      if (layer && material.uniforms['useWallMaskUv']) {
        material.uniforms['useWallMaskUv'].value = layer.surface === 'wall' ? 1 : 0;
      }
      if (layer && material.uniforms['rawFloorPlankMode']) {
        material.uniforms['rawFloorPlankMode'].value = this.shouldUseRawFloorPlankShaderForLayer(layer) ? 1 : 0;
      }
      if (layer && material.uniforms['rotationRad']) {
        material.uniforms['rotationRad'].value = this.patternRotationRadForLayer(layer);
      }
      if (layer && material.uniforms['groutEnabled']) {
        material.uniforms['groutEnabled'].value = this.groutEnabledForLayer(layer) ? 1 : 0;
      }
      if (layer && material.uniforms['groutLineSize']) {
        const groutLineSize = material.uniforms['groutLineSize'].value as THREE.Vector2;
        const nextGroutLineSize = this.groutLineSizeForLayer(layer);
        if (groutLineSize?.copy) {
          groutLineSize.copy(nextGroutLineSize);
        } else {
          material.uniforms['groutLineSize'].value = nextGroutLineSize;
        }
      }
      if (layer && material.uniforms['groutColor']) {
        const groutColor = material.uniforms['groutColor'].value as THREE.Color;
        const nextGroutColor = this.groutColorForLayer(layer);
        if (groutColor?.copy) {
          groutColor.copy(nextGroutColor);
        } else {
          material.uniforms['groutColor'].value = nextGroutColor;
        }
      }
      material.uniforms['compareX'].value = this.clampNumber(this.comparePosition, 0, 1);
      material.uniforms['isCompare'].value = this.mode === 'compare' ? 1 : 0;
      material.needsUpdate = true;
    }
  }

  if (this.sliceMesh) {
    // Untuk preview layer multi-instance, mask shader sudah jadi clip utama.
    // Slice adalah cutout original image; kalau ditaruh di atas preview,
    // hasilnya kelihatan seperti double/inverse layer.
    this.sliceMesh.visible = false;
  }

  if (this.baseMesh) {
    this.baseMesh.visible = true;
  }
}

  private resizeRenderer(): void {
    if (!this.renderer || !this.camera) {
      return;
    }
    const host = this.hostRef.nativeElement;
    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    this.renderer.setSize(width, height, false);
    this.camera.left = -width / 2;
    this.camera.right = width / 2;
    this.camera.top = height / 2;
    this.camera.bottom = -height / 2;
    this.camera.updateProjectionMatrix();
    this.applyCameraFit();
  }

  private applyCameraFit(): void {
    if (!this.imageGroup || !this.hostRef || this.imageWidth <= 0 || this.imageHeight <= 0) {
      this.updateScreenHotspots();
      this.scheduleRender();
      return;
    }

    const host = this.hostRef.nativeElement;
    const availableWidth = Math.max(1, host.clientWidth - 32);
    const availableHeight = Math.max(1, host.clientHeight - 32);
    this.fitScale = Math.min(availableWidth / this.imageWidth, availableHeight / this.imageHeight);

    if (this.mode === 'compare') {
      this.zoom = 1;
      this.pan = { x: 0, y: 0 };
    }

    const scale = this.fitScale * this.zoom;
    this.imageGroup.scale.set(scale, scale, 1);
    this.imageGroup.position.set(this.pan.x, this.pan.y, 0);

    this.updateScreenHotspots();
    this.scheduleRender();
  }

  private updateScreenHotspots(): void {
    if (!this.hostRef?.nativeElement || this.imageWidth <= 0 || this.imageHeight <= 0) {
      this.screenHotspots = [];
      return;
    }

    this.screenHotspots = this.surfaceHotspots.map((hotspot) => {
      const position = this.imageToScreen({
        x: hotspot.xRatio * this.imageWidth,
        y: hotspot.yRatio * this.imageHeight
      });
      const polygonPointList = (hotspot.polygon || [])
        .map((point, index) => ({
          ...this.imageToScreen(point),
          label: ['TL', 'TR', 'BR', 'BL'][index] || String(index + 1)
        }));
      const polygonPoints = polygonPointList
        .map((point) => `${point.x},${point.y}`)
        .join(' ');

      return {
        ...hotspot,
        x: position.x,
        y: position.y,
        polygonPoints,
        polygonPointList
      };
    });
  }

  private scheduleRender(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.animationFrameId = requestAnimationFrame(() => {
      this.animationFrameId = 0;
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    });
  }

  private loadTexture(url: string, fallbackUrl: string | null = null): Promise<THREE.Texture> {
    this.textureLoader.setCrossOrigin('anonymous');

    const loadOnce = (sourceUrl: string): Promise<THREE.Texture> => {
      const key = this.textureCacheKey(sourceUrl);
      const cached = this.textureCache.get(key);

      if (cached) {
        return cached;
      }

      const request = new Promise<THREE.Texture>((resolve, reject) => {
        this.textureLoader.load(
          sourceUrl,
          (texture) => {
            this.cachedTextures.add(texture);
            resolve(texture);
          },
          undefined,
          reject
        );
      }).catch((error) => {
        this.textureCache.delete(key);
        throw error;
      });

      this.textureCache.set(key, request);
      return request;
    };

    return loadOnce(url).catch((error) => {
      if (!fallbackUrl || fallbackUrl === url) {
        throw error;
      }

      console.warn('Texture gagal dimuat, pakai fallback dulu:', url, error);
      return loadOnce(fallbackUrl);
    });
  }

  private textureCacheKey(url: string): string {
    return String(url || '').trim();
  }

  private disposeTextureIfDisposable(texture: THREE.Texture | null | undefined): void {
    if (!texture) {
      return;
    }

    // Texture dari URL cache dipakai ulang saat offset/rotasi/surface berubah.
    // Jangan dispose di rebuild scene, kalau tidak browser akan load ulang asset yang sama.
    if (this.cachedTextures.has(texture)) {
      return;
    }

    texture.dispose();
  }

  private disposeTextureCache(): void {
    for (const texturePromise of this.textureCache.values()) {
      texturePromise
        .then((texture) => texture.dispose())
        .catch(() => {
          // ignore cache load failures
        });
    }

    this.textureCache.clear();
  }

  private configureTexture(texture: THREE.Texture, repeat = false): void {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
    texture.wrapT = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
    texture.needsUpdate = true;
  }

  private textureSize(texture: THREE.Texture): { width: number; height: number } {
    const image = texture.image as { width?: number; height?: number };
    return {
      width: Number(image.width ?? 1),
      height: Number(image.height ?? 1)
    };
  }

  private disposeSceneObjects(): void {
    if (!this.imageGroup) {
      return;
    }
    while (this.imageGroup.children.length) {
      const child = this.imageGroup.children.pop();
      if (!child) continue;
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        for (const material of materials) {
          const withMap = material as THREE.Material & { map?: THREE.Texture; uniforms?: Record<string, { value: unknown }> };
          if (withMap.map) {
            this.disposeTextureIfDisposable(withMap.map);
          }
          const resultMap = withMap.uniforms?.['resultMap']?.value;
          if (resultMap instanceof THREE.Texture) {
            this.disposeTextureIfDisposable(resultMap);
          }
          material.dispose();
        }
      }
    }
    for (const texture of this.previewLayerTextures) {
      this.disposeTextureIfDisposable(texture);
    }

    this.previewLayerTextures = [];
    this.previewLayerMaterials = [];
    this.previewLayerMeshes = [];

    this.baseMesh = null;
    this.resultMesh = null;
    this.previewMesh = null;
    this.sliceMesh = null;
    this.resultMaterial = null;
    this.previewMaterial = null;
    this.disposeTextureIfDisposable(this.previewTexture);
    this.previewTexture = null;
    this.disposeTextureIfDisposable(this.sourceProductTexture);
    this.sourceProductTexture = null;
    this.disposeTextureIfDisposable(this.maskTexture);
this.maskTexture = null;
  }

  private getEditablePointsOrDefault(): PerspectivePoints {
    return this.clonePoints(this.editablePoints) ?? this.defaultQuad();
  }

  private defaultQuad(): PerspectivePoints {
    const width = this.imageWidth || 1200;
    const height = this.imageHeight || 800;
    return {
      topLeft: { x: Math.round(width * 0.18), y: Math.round(height * 0.52) },
      topRight: { x: Math.round(width * 0.82), y: Math.round(height * 0.52) },
      bottomRight: { x: Math.round(width * 0.98), y: Math.round(height * 0.96) },
      bottomLeft: { x: Math.round(width * 0.02), y: Math.round(height * 0.96) }
    };
  }

  private clonePoints(points: PerspectivePoints | null | undefined): PerspectivePoints | null {
    if (!points) {
      return null;
    }
    return {
      topLeft: { ...points.topLeft },
      topRight: { ...points.topRight },
      bottomRight: { ...points.bottomRight },
      bottomLeft: { ...points.bottomLeft }
    };
  }

  private imagePointToWorld(point: Point2D): Point2D {
    return {
      x: point.x - this.imageWidth / 2,
      y: this.imageHeight / 2 - point.y
    };
  }

  private imageToScreen(point: Point2D): Point2D {
    const host = this.hostRef.nativeElement;
    const scale = this.fitScale * this.zoom;
    const world = this.imagePointToWorld(point);
    return {
      x: host.clientWidth / 2 + this.pan.x + world.x * scale,
      y: host.clientHeight / 2 - this.pan.y - world.y * scale
    };
  }

  private screenToImage(clientX: number, clientY: number): Point2D {
    const host = this.hostRef.nativeElement;
    const rect = host.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const scale = this.fitScale * this.zoom || 1;
    const worldX = (x - host.clientWidth / 2 - this.pan.x) / scale;
    const worldY = -(y - host.clientHeight / 2 + this.pan.y) / scale;
    return {
      x: worldX + this.imageWidth / 2,
      y: this.imageHeight / 2 - worldY
    };
  }

  private getDisplayedImageRect(): { left: number; top: number; width: number; height: number } | null {
    if (!this.hostRef?.nativeElement || this.imageWidth <= 0 || this.imageHeight <= 0) {
      return null;
    }
    const host = this.hostRef.nativeElement;
    const scale = this.fitScale * this.zoom;
    const width = this.imageWidth * scale;
    const height = this.imageHeight * scale;
    return {
      left: host.clientWidth / 2 + this.pan.x - width / 2,
      top: host.clientHeight / 2 - this.pan.y - height / 2,
      width,
      height
    };
  }

  private updateComparePositionFromPointer(event: PointerEvent): void {
    const hostRect = this.hostRef.nativeElement.getBoundingClientRect();
    const imageRect = this.getDisplayedImageRect();
    if (!imageRect || imageRect.width <= 0) {
      return;
    }
    const pointerXInsideHost = event.clientX - hostRect.left;
    const position = (pointerXInsideHost - imageRect.left) / imageRect.width;
    const clampedPosition = this.clampNumber(position, 0, 1);
    this.comparePosition = clampedPosition;
    this.comparePositionChange.emit(clampedPosition);
    this.applyRuntimeUniforms();
    this.scheduleRender();
  }

  private clampNumber(value: number, min: number, max: number): number {
    return Math.min(Math.max(Number(value) || 0, min), max);
  }
}
